import * as turf from '@turf/intersect';
import {union, point, distance, area} from '@turf/turf';
import * as turfhelp from '@turf/helpers';
import {DateTime, Interval} from 'luxon';
import mapboxgl from "mapbox-gl";

// Validate that polygon coordinates have at least 4 positions per ring
function isValidPolygonCoordinates(coordinates, geometryType) {
    if (!coordinates || !Array.isArray(coordinates)) {
        return false;
    }

    if (geometryType === 'Polygon') {
        // For Polygon: coordinates is an array of rings
        return coordinates.every(ring => Array.isArray(ring) && ring.length >= 4);
    } else if (geometryType === 'MultiPolygon') {
        // For MultiPolygon: coordinates is an array of polygons, each with rings
        return coordinates.every(polygon =>
            Array.isArray(polygon) && polygon.every(ring => Array.isArray(ring) && ring.length >= 4)
        );
    }

    return false;
}

/**
 * Create a fast hash key for layer objects to replace slow JSON.stringify deduplication
 * Optimization Item #7: 2-3x faster deduplication than JSON.stringify
 * @param {Object} item - Layer object with layerId, status, description, etc.
 * @returns {string} - Composite hash key
 */
function createLayerHashKey(item) {
    // Create composite key from critical properties
    // Using template literals is ~3x faster than JSON.stringify for deduplication
    const descPreview = item.description ? String(item.description).slice(0, 100) : '';
    const status = item.status || '';
    const layerId = item.layerId || '';
    const ceiling = item.ceiling || '';
    const classType = item.class || '';
    const uniqueId = item.tfrId || item.notam_id || '';
    const startDate = item.start_datetime || '';
    const endDate = item.end_datetime || '';

    return `${layerId}|${status}|${uniqueId}|${classType}|${ceiling}|${startDate}|${endDate}|${descPreview}`;
}

/**
 * Create a hash key for geometry objects (faster than JSON.stringify)
 * @param {Object} geom - GeoJSON geometry or feature with geometry
 * @returns {string} - Hash key based on geometry type and coordinate sample
 */
function createGeometryHashKey(geom) {
    if (!geom || !geom.geometry) return 'null';
    const coords = geom.geometry.coordinates;
    if (!coords || !Array.isArray(coords)) return 'empty';

    // Sample first and last coordinates for hash (good enough for deduplication)
    const flatCoords = JSON.stringify(coords).slice(0, 200);
    return `${geom.geometry.type}|${flatCoords}`;
}

function BusinessLogic(features, selectedDateTime, latLong, duration, selectedDate, selectedTime, selectedTimezone, operation_mode, suntime, airports, mapref, selectedArea_circle, proximity2NMResults = []) {

    // authorization details
    let authorizations_time_schedule_container = []; // authorizations container
    let schedule_time_array = []; // all schedule times to support mutliple schedule airspaces
    let auth_overlap_schedule = []; // determines unique authorizations
    let auth_unique_uasfm_touched = []; // common for all authorizations
    let auth_unique_class_airspace_touched = []; // common for all authorizations
    let notam_flag_detected = false;

    // Optimization Item #10: Local MTR routes collection instead of window.mtrRoutes
    // Makes function pure, enables caching, fixes non-deterministic bugs
    const mtrRoutes = new Set();

    if (!features || !Array.isArray(features)) {
        return []; // Return an empty array if features is undefined or not an array
    }

    // console.log("entering-business-logic");
    // console.log(features);
    //console.log(selectedDateTime, latLong, duration);

    //store final layers
    let return_layers = [];

    // pre processing for business logic ==================================
    //console.log("pre processing");

    // Optimization Item #8: Categorize all features in a single pass (5x faster than multiple filter passes)
    const env = process.env.REACT_APP_ENV;
    const categorizedFeatures = {
        uasfm: [],
        classB: [],
        classE: [],
        dc: [],
        nsufr: [],
        pnsufr: [],
        specialUse: [],
        seams: [],
        b4ufly: [],
        tfr: [],
        other: []
    };

    // Single pass to categorize all features by layer type
    features.forEach(layer => {
        const id = layer.layerId.toLowerCase();

        // Categorize based on layer ID
        if ((['qa', 'dev'].includes(env) && id.includes("qa-laanc-uasfm")) || id.includes("laanc-uasfm")) {
            categorizedFeatures.uasfm.push(layer);
        } else if (id.includes("laanc-airspace-class-b") || id.includes("laanc-airspace-class-c") ||
                   id.includes("laanc-airspace-class-d")) {
            categorizedFeatures.classB.push(layer);
        } else if (id.includes("laanc-airspace-class-e")) {
            categorizedFeatures.classE.push(layer);
        } else if (id.includes("laanc-dc")) {
            categorizedFeatures.dc.push(layer);
        } else if (id.includes("laanc-nsufr")) {
            categorizedFeatures.nsufr.push(layer);
        } else if (id.includes("laanc-pnsufr")) {
            categorizedFeatures.pnsufr.push(layer);
        } else if (id.includes("laanc-specialuse")) {
            categorizedFeatures.specialUse.push(layer);
        } else if (id.includes("laanc-seams")) {
            categorizedFeatures.seams.push(layer);
        } else if (id.includes("laanc-b4ufly")) {
            categorizedFeatures.b4ufly.push(layer);
        } else if (id.includes("laanc-tfr")) {
            categorizedFeatures.tfr.push(layer);
        } else {
            categorizedFeatures.other.push(layer);
        }
    });

    // Process UASFM ===========================================================================
    let tempMaxAltitude = 400;
    let uasfm_layer_entry = {};
    let multiple_uasfm_hights_detected = false;
    let prev_uasfm_height = -100;
    let uasfm_container = [];
    categorizedFeatures.uasfm.forEach((layer, index_layer) => {

        const uasfm_items = convertFieldsToStructuredData(layer.properties);

        // Logic to find the lowest character in AIRSPACE and its indices
        let lowestChar = null;
        let lowestCharIndices = [];

        // identify the lowest airspace class where multiple airspaces present
        if (uasfm_items.AIRSPACE && uasfm_items.AIRSPACE.length > 0) {
            uasfm_items.AIRSPACE.forEach((airspace, index) => {
                if (lowestChar === null || airspace < lowestChar) {
                    lowestChar = airspace;
                    lowestCharIndices = [index];
                } else if (airspace === lowestChar) {
                    lowestCharIndices.push(index);
                }

                //console.log(uasfm_items.faaids[index]);

                // save UASFM OBJECTID, LAST_EDIT
                addObjectIfNotExists_UASFM(auth_unique_uasfm_touched, {
                    OBJECTID: layer.properties.OBJECTID,
                    LAST_EDIT: layer.properties.LAST_EDIT,
                    AIRSPACE: uasfm_items.AIRSPACE[index],
                    APT_FAAID: uasfm_items.faaids[index],
                    APT_ICAO: uasfm_items.icaos[index],
                    GLOBALID: layer.properties.GLOBALID
                }, (a, b) => ((a.OBJECTID === b.OBJECTID) && (a.APT_FAAID === b.APT_FAAID)));

            });
        }

        //console.log(lowestCharIndices);

        let class_description = 'Automatic Approval for LAANC Authorization is available in airspace';
        let class_status = 'ALERT';
        let uasfm_airspace = ''; // if muliple items, all the airspace values are same
        lowestCharIndices.forEach((index) => {

            // determine max altitude for the operation
            if (tempMaxAltitude > layer.properties.CEILING) {
                tempMaxAltitude = layer.properties.CEILING;
            }

            //console.log("tempMaxAltitude:" + tempMaxAltitude);

            // default values
            class_description += ` (${uasfm_items.faaids[index]}/${uasfm_items.icaos[index]}) CLASS ${uasfm_items.AIRSPACE[index]}  up to ${tempMaxAltitude} ft. `;
            class_status = "ALERT";
            uasfm_airspace = uasfm_items.AIRSPACE[index];
            // if (tempMaxAltitude > 0 && uasfm_items.laanc[index] === 1) {
            //     uasfm_layer_entry = {
            //         layerId: "UAS Facility Map",
            //         description: class_description,
            //         status: class_status
            //     };
            //
            // } else
            if (tempMaxAltitude === 0 && uasfm_items.laanc[index] === 1) {
                class_description = `Automatic Approval for LAANC Authorization is not available for the selected area due to maximum allowable altitude restrictions`;
                class_status = "NOK";
            } else if (layer.properties.APT1_LAANC === 0) {
                class_description = `Automatic Approval for LAANC Authorization is not available for the selected area`;
                class_status = "NOK";
            }

            // detect grids with different heights
            if (index_layer > 0) {
                if (prev_uasfm_height !== layer.properties.CEILING)
                    multiple_uasfm_hights_detected = true;
            }

            // save to previous
            prev_uasfm_height = layer.properties.CEILING;
        });

        if (class_description && class_status && uasfm_airspace) { //|| uasfm_airspace <= uasfm_layer_entry.class || class_status === "NOK"
            uasfm_layer_entry = {
                layerId: "UAS Facility Map",
                description: class_description,
                status: class_status,
                class: uasfm_airspace,
                ceiling: layer.properties.CEILING,
                indices: lowestCharIndices.length
            };
            uasfm_container.push({...uasfm_layer_entry});
        }
    });

    //console.log(uasfm_container);

    // add to the return array if not empty object
    if (uasfm_container.length > 0) {
        // check whether any
        if (uasfm_container.some(item => item.status === "NOK")) {
            //console.log("NOK items found");
            // all NOK advisories
            const nok_uasfm_advisroeis = uasfm_container.filter(item => item.status === "NOK");

            // filter only unique (Optimization Item #7: Use custom hash instead of JSON.stringify)
            const unique_nok_uasfm = [
                ...new Map(nok_uasfm_advisroeis.map(item => [createLayerHashKey(item), item])).values()
            ];

            const unique_descriptions = Array.from(
                new Set(unique_nok_uasfm.map(item => item.description)) // Create a set of unique descriptions
            ).map(description =>
                unique_nok_uasfm.find(item => item.description === description) // Find the first object with the unique description
            );

            return_layers.push(...unique_descriptions);

        } else {
            //console.log("NOK items not found");

            // Process and group by airspace and class, keeping the lowest ft value
            const unique_uasfm_filter_one = Object.values(
                uasfm_container.reduce((acc, obj) => {
                    // Extract all airspace, class, and ft combinations
                    const details = extractDetails(obj.description);

                    details.forEach(({airspace, class: classType, ft}) => {
                        const key = `${airspace}-${classType}`; // Create a key based on airspace and class

                        // If no entry exists or the current ft is lower, update the entry
                        if (!acc[key] || acc[key].ft > ft) {
                            acc[key] = {...obj, airspace, class: classType, ft};
                        }
                    });

                    return acc;
                }, {})
            );

            const unique_uasfm_filter_two = Array.from(
                new Set(unique_uasfm_filter_one.map(item => item.description)) // Create a set of unique descriptions
            ).map(description =>
                unique_uasfm_filter_one.find(item => item.description === description) // Find the first object with the unique description
            );

            //     Array.from(
            //     new Set(unique_uasfm_filter_one.map(item => JSON.stringify(item))) // Create a set of unique JSON strings
            // ).map(item => JSON.parse(item));

            unique_uasfm_filter_two.forEach((uasfm_display_obj, index) => {
                // update uasfm layer description to include Flyzone notice
                if (multiple_uasfm_hights_detected) {
                    uasfm_display_obj.flyzone_notice = `UASFM grids with multiple altitudes detected. Maximum selectable height adjusted to the lowest ceiling. Currently Flyzone does not support Further Coordination (FC) requests. Further Coordination is available for this operation through other USSs <a href="https://www.faa.gov/uas/getting_started/laanc" target="_blank" rel="noopener noreferrer">https://www.faa.gov/uas/getting_started/laanc</a>`
                }

                return_layers.push({...uasfm_display_obj});
            });
        }


    }


    // End UASFM Processing =====================================================================


    // Start Class Airspace Processing ================================
    // CLASS C and D
    let class_b_c_d_layer_entry = {};


    // set mission start date and time
    const [sel_year, sel_month, sel_day] = selectedDate.split('-').map(Number);
    const [sel_hours, sel_minutes] = selectedTime.split(':').map(Number);
    const selected_start_date_obj = new Date(sel_year, sel_month - 1, sel_day, sel_hours, sel_minutes);

    // set mission end date and time
    const selected_end_date_obj = addMinutesToDate(selected_start_date_obj, duration);
    //console.log(selectedTime);
    //console.log(selected_start_date_obj);
    //console.log(duration);
    //console.log(selected_end_date_obj);

    // determine LAANC availability from UASFM
    let isLaancAvailable = true;
    if (uasfm_container.length > 0) {
        isLaancAvailable = !uasfm_container.some(item => item.status === "NOK");
    }

    // loop through class c or class d, TODO: determine priority
    // Optimization Item #8: Use pre-categorized features instead of filtering
    categorizedFeatures.classB.forEach((layer, index) => {

        if (layer.properties.LOWER_VAL === 0) {
            let schedule_time = {}; // schedule temp storage

            // insert non-duplicate class airspace with LOWER_VAL === 0
            addObjectIfNotExists(auth_unique_class_airspace_touched, {
                LOWER_VAL: layer.properties.LOWER_VAL,
                OBJECTID: layer.properties.OBJECTID,
                LOCAL_TYPE: layer.properties.LOCAL_TYPE,
                CLASS: layer.properties.CLASS,
                IDENT: layer.properties.IDENT,
                ICAO_ID: layer.properties.ICAO_ID,
                ADHP_ID: layer.properties.ADHP_ID
            }, (a, b) => a.OBJECTID === b.OBJECTID);

            // schedule airspaces
            if (layer.properties.schedule) {

                let schedule_json = JSON.parse(layer.properties.schedule);

                if (Array.isArray(schedule_json.schedule.Timesheet)) { // schedule varies through the year

                    // loop through
                    schedule_json.schedule.Timesheet.forEach((schedule_entry, index) => {
                        const [month1, day1] = schedule_entry.startDate.split('-').map(Number);
                        const [month2, day2] = schedule_entry.endDate.split('-').map(Number);
                        const sched_start = new Date(sel_year, month1 - 1, day1);
                        const sched_end = new Date(sel_year, month2 - 1, day2);

                        // select the schedule applicable for the selected date
                        if (selected_start_date_obj >= sched_start && selected_start_date_obj <= sched_end) {
                            schedule_time = {
                                startTime: schedule_entry.startTime,
                                endTime: schedule_entry.endTime,
                                OBJECTID: layer.properties.OBJECTID
                            };
                        }
                    });

                } else { // one schedule for the entire year
                    const [month1, day1] = schedule_json.schedule.Timesheet.startDate.split('-').map(Number);
                    const [month2, day2] = schedule_json.schedule.Timesheet.endDate.split('-').map(Number);
                    const sched_start = new Date(sel_year, month1 - 1, day1);
                    const sched_end = new Date(sel_year, month2 - 1, day2);

                    if (selected_start_date_obj >= sched_start && selected_start_date_obj <= sched_end) {
                        schedule_time = {
                            startTime: schedule_json.schedule.Timesheet.startTime,
                            endTime: schedule_json.schedule.Timesheet.endTime,
                            OBJECTID: layer.properties.OBJECTID
                        };
                    }
                }

                let description = isLaancAvailable
                    ? `<p>Selected operation area overlaps with controlled Airspace ${layer.properties.IDENT}/${layer.properties.ICAO_ID}. You must have an airspace authorization from the FAA. Use LAANC.</p>
                       <p>Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`
                    : `<p>Selected operation area overlaps with controlled Airspace ${layer.properties.IDENT}/${layer.properties.ICAO_ID}. You must have an airspace authorization from the FAA. LAANC is not presently available in this area, use FAA DroneZone.</p>
                       <p>Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`;
                class_b_c_d_layer_entry = {
                    layerId: "Scheduled Airspace",
                    displayName: "Controlled Airspace",
                    description: description,
                    status: "CAUTION",
                    flyzone_notice: `Airspace is active between ${schedule_time.startTime}hrs and ${schedule_time.endTime}hrs, for the selected date`
                };

                return_layers.push({...class_b_c_d_layer_entry});

            } else { // airspaces without schedule (full time)

                let description = isLaancAvailable
                    ? `<p>Selected operation area overlaps with controlled Airspace ${layer.properties.IDENT}/${layer.properties.ICAO_ID}. You must have an airspace authorization from the FAA. Use LAANC.</p>
                       <p>Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`
                    : `<p>Selected operation area overlaps with controlled Airspace ${layer.properties.IDENT}/${layer.properties.ICAO_ID}. You must have an airspace authorization from the FAA. LAANC is not presently available in this area, use FAA DroneZone.</p>
                       <p>Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`;
                class_b_c_d_layer_entry = {
                    layerId: "Class Airspace",
                    displayName: "Controlled Airspace",
                    description: description,
                    status: "ALERT"
                };
                schedule_time = {
                    startTime: '00:00',
                    endTime: '23:59',
                    OBJECTID: layer.properties.OBJECTID
                };

                return_layers.push({...class_b_c_d_layer_entry});
            }

            // check for notam flag
            if (layer.properties.WKHR_CODE === "NOTAM") {
                notam_flag_detected = true;
            }

            // add to schedule time array
            if (Object.keys(schedule_time).length !== 0) {
                schedule_time_array.push(schedule_time);
            }

        }


    });

    // CLASS E airspace, only consider category E2 cases
    let class_e2_layer_entry = {};
    // Optimization Item #8: Use pre-categorized features
    categorizedFeatures.classE.forEach((layer, index) => {

        // insert non-duplicate class airspace with LOWER_VAL === 0
        addObjectIfNotExists(auth_unique_class_airspace_touched, {
            LOWER_VAL: layer.properties.LOWER_VAL, // for validity
            OBJECTID: layer.properties.OBJECTID, // use to filter map features
            LOCAL_TYPE: layer.properties.LOCAL_TYPE, // detect class E2
            CLASS: layer.properties.CLASS, // for authorization
            IDENT: layer.properties.IDENT, // for authorization
            ICAO_ID: layer.properties.ICAO_ID, // for authorization
            ADHP_ID: layer.properties.ADHP_ID // for authorization
        }, (a, b) => a.OBJECTID === b.OBJECTID);

        if (layer.properties.LOCAL_TYPE === "CLASS_E2") {

            let schedule_time = {
                startTime: '00:00',
                endTime: '23:59',
                OBJECTID: layer.properties.OBJECTID
            };

            class_e2_layer_entry = {
                layerId: "Class E Weather Warning",
                description: 'LAANC Authorization is invalid when the weather ceiling is less than 1,000 ft AGL.',
                status: "ALERT",
            };

            // add to schedule time array
            if (Object.keys(schedule_time).length !== 0) {
                schedule_time_array.push(schedule_time);
            }

            if (Object.keys(class_e2_layer_entry).length !== 0) {
                return_layers.push({...class_e2_layer_entry});
            }
        }

    });


    //&& Object.keys(uasfm_layer_entry).length !== 0
    if (auth_unique_class_airspace_touched.length === 0 && tempMaxAltitude !== 0) { //TODO: update to handle uasfm array    handle case where UASFM outside

        //remove the uasfm entry from return array
        return_layers = return_layers.filter(obj => obj.layerId !== uasfm_layer_entry.layerId);

        return_layers.push({
            layerId: "Uncontrolled Airspace",
            description: 'LAANC Authorization(s) not required',
            status: "OK",
        });
    }

    // End Class Airspace Processing ==============================================================================

    // Check if within controlled airspace (B, C, D, or E) and UASFM has NOK entries
    const isInControlledAirspace = features.some(layer =>
        layer.layerId.toLowerCase().includes("laanc-airspace-class-b") ||
        layer.layerId.toLowerCase().includes("laanc-airspace-class-c") ||
        layer.layerId.toLowerCase().includes("laanc-airspace-class-d") ||
        layer.layerId.toLowerCase().includes("laanc-airspace-class-e")
    );

    const hasUasfmNOK = uasfm_container.some(entry => entry.status === "NOK");

    if (isInControlledAirspace && hasUasfmNOK) {
        return_layers.push({
            layerId: "Controlled Airspace (LAANC Inactive)",
            displayName: "Controlled Airspace (LAANC Inactive)",
            description: `<p>You must have an airspace authorization from the FAA. LAANC is not presently available in this area, use FAADroneZone.</p>
                          <p>Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`,
            status: "CAUTION",
            flyzone_notice: `<a href="https://faadronezone-access.faa.gov" target="_blank" rel="noopener noreferrer">Go To FAADroneZone</a>`
        });
    }

    if (notam_flag_detected) {
        return_layers.push({
            layerId: "Notices to Air Missions Warning",
            description: `Review NOTAMs applicable to your operation area`,
            status: "OK",
            flyzone_notice: `<a href="https://notams.aim.faa.gov/notamSearch" target="_blank" rel="noopener noreferrer">https://notams.aim.faa.gov/notamSearch</a>`
        });
    }

    // DC Special Rules (SFRA) - Intersecting
    // Check if SFRA is detected from proximity2NMResults (intersecting layers)
    const sfraNearbyLayer = proximity2NMResults.find(p => p.layerId === 'dc-sfra' && p.intersectsOperation);

    if (sfraNearbyLayer) {
        return_layers.push({
            layerId: "Special Flight Rules Area (SFRA)",
            displayName: "Special Flight Rules Area",
            description: `
                          <p>Flying a drone for recreational or nonrecreational use between 15 and 30 miles from Washington, D.C. is allowed under specific operating conditions.</p>
                          <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
            status: "CAUTION",
            flyzone_notice: `<a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/dc" target="_blank" rel="noopener noreferrer">FAA Resource</a>`
        });
    }

    // laanc-DC FRZ
    // Optimization Item #8: Use pre-categorized features
    categorizedFeatures.dc.forEach((layer, index) => {
        return_layers.push({
            layerId: "Drone Operations Restricted Area",
            displayName: "Flight Restricted Airspace",
            description: `
                          <p>Operation overlaps DC FRZ, and no person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                          <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
            status: "DNF",
            flyzone_notice: `<a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/dc" target="_blank" rel="noopener noreferrer">View FAA Resource</a>`
        });
    });

    // NSUFR Full-Time National Security UAS Flight Restrictions
    let full_time_nsufr_entry = {};
    // Optimization Item #8: Use pre-categorized features
    categorizedFeatures.nsufr.forEach((layer, index) => {
        full_time_nsufr_entry = {
            layerId: "National Security Restrictions (NSUFR)",
            displayName: "Flight Restricted Airspace",
            description: `
                          <p>Operation overlaps with ${(layer.properties.Base?.trim() || layer.properties.Facility)}. and no person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                          <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
            status: "DNF",
            flyzone_notice: `The POC for this airspace is ${layer.properties.POC}`
        };

    });
    if (Object.keys(full_time_nsufr_entry).length !== 0) {
        return_layers.push(full_time_nsufr_entry);
    }


    let part_time_nsufr_entry = {};
    // Optimization Item #8: Use pre-categorized features
    categorizedFeatures.pnsufr.forEach((layer, index) => {
        if (isEpochOverlap(formatDateToYYYYMMDDHHMM(selected_start_date_obj), formatDateToYYYYMMDDHHMM(selected_end_date_obj), selectedTimezone, layer.properties.ACTIVETIME, layer.properties.ENDTIME)) {
            part_time_nsufr_entry = {
                layerId: "National Security Restrictions (NSUFR)",
                displayName: "Flight Restricted Airspace",
                description: `Operation overlaps with ${layer.properties.Base}, and no person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.`,
                status: "DNF",
                flyzone_notice: `The POC for this airspace is ${layer.properties.POC}`
            };
            return_layers.push(part_time_nsufr_entry);
        }
    });
    // if (Object.keys(part_time_nsufr_entry).length !== 0) {
    //     return_layers.push(part_time_nsufr_entry);
    // }


    // END NSUFR

// Special Use Airspace
    let sua_entry = {};

    // Optimization Item #8: Use pre-categorized features
    categorizedFeatures.specialUse.forEach((layer, index) => {

            const typeCode = layer.properties.TYPE_CODE;

            // restricted, prohibited
            if (typeCode === "P" || typeCode === "R") {

                const type = (typeCode === "P") ? 'Prohibited' : 'Restricted';

                sua_entry = {
                    layerId: `${type} Special Use Airspace (SUA)`,
                    displayName: "Flight Restricted Airspace",
                    description: `<p>Operation overlaps with ${layer.properties.NAME} SUA. and no person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                                  <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                    status: "DNF",
                    flyzone_notice: `<a href="https://sua.faa.gov/" target="_blank" rel="noopener noreferrer">https://sua.faa.gov/</a>`
                };

            }
            // A, MOA, W → CAUTION
            else if (typeCode === "A" || typeCode === "MOA" || typeCode === "W") {

                let type = "";
                if (typeCode === "A") type = "Alert Area";
                if (typeCode === "MOA") type = "Military Training Area";
                if (typeCode === "W") type = "Warning Area";

                sua_entry = {
                    layerId: `${type} Special Use Airspace (SUA)`,
                    displayName: "Special Use Airspace",
                    description: `<p>Operation overlaps with ${layer.properties.NAME} SUA (${type}).</p>
                                  <p>This airspace may contain more air traffic or fast military aircraft.Maintain extra vigilance and awareness when flying.</p>
                                  <p>Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`,
                    status: "CAUTION",
                    flyzone_notice: `<a href="https://sua.faa.gov/" target="_blank" rel="noopener noreferrer">https://sua.faa.gov/</a>`
                };
            }

            if (Object.keys(sua_entry).length !== 0) {
                return_layers.push(sua_entry);
            }
        });

    //  End SUA

    // Stadium layer
    //let class_stadium_layer_entry = {};
    //features.filter(layer => layer.layerId.toLowerCase().includes("laanc-stadium")).forEach((layer, index) => {

    //class_stadium_layer_entry = {
    //layerId: "Stadium",
    //description: `Flight operations within 3 NM of an FAA-designated stadium are restricted from 1 hour before the planned start time until 1 hour after the end of any qualifying event. Check schedule for ${layer.properties.NAME}.`,
    //status: "CAUTION",
    //flyzone_notice: `<p><a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/stadiums" target="_blank" rel="noopener noreferrer">FAA Resources</a></p>`
    //};
    //handle maltiple nearby stadiums
    //if (Object.keys(class_stadium_layer_entry).length !== 0) {
    //return_layers.push(class_stadium_layer_entry);
    //}

    //});

    //SEAMS- Stadium

function formatSeamsDateTime(rawValue) {
    const ms = Number(rawValue);
    if (!Number.isFinite(ms)) {
        return 'N/A';
    }

    return DateTime.fromMillis(ms, {zone: 'utc'}).toUTC().toFormat("yyyy-MM-dd HH:mm 'UTC'");
}

function getSeamsDisplayEventInfo(stadiumEvents, selectedDate, selectedTime, selectedTimezone) {
    if (!Array.isArray(stadiumEvents) || stadiumEvents.length === 0) {
        return null;
    }

    const selectedStartDateTimeUTC = DateTime.fromFormat(
        `${selectedDate} ${selectedTime}`,
        'yyyy-MM-dd HH:mm',
        {zone: selectedTimezone}
    ).toUTC();

    const candidates = stadiumEvents
        .map((event) => {
            const gameDateMs = Number(event?.props?.GAME_DATE);
            const gameDateUTC = Number.isFinite(gameDateMs)
                ? DateTime.fromMillis(gameDateMs, {zone: 'utc'})
                : null;

            if (!gameDateUTC) {
                return null;
            }

            return {
                ...event,
                gameDateUTC,
                diffMs: Math.abs(gameDateUTC.toMillis() - selectedStartDateTimeUTC.toMillis()),
                isFuture: selectedStartDateTimeUTC < gameDateUTC
            };
        })
        .filter(Boolean);

    if (candidates.length === 0) {
        return null;
    }

    const futureEvents = candidates.filter(event => event.isFuture);
    const selectedCandidate = futureEvents.length > 0
        ? futureEvents.reduce((closest, event) => event.diffMs < closest.diffMs ? event : closest, futureEvents[0])
        : candidates.reduce((closest, event) => event.diffMs < closest.diffMs ? event : closest, candidates[0]);

    return {
        eventName: selectedCandidate.props?.EVENT_NAME || 'a scheduled event',
        venue: selectedCandidate.props?.VENUE || selectedCandidate.props?.GAME_DETAIL_FACILITY_NAME || 'an FAA-designated stadium',
        gameDate: selectedCandidate.props?.GAME_DATE,
        endDate: selectedCandidate.props?.END_DATE,
        gameDateDisplay: formatSeamsDateTime(selectedCandidate.props?.GAME_DATE),
        endDateDisplay: formatSeamsDateTime(selectedCandidate.props?.END_DATE)
    };
}

    //SEAMS- Stadium

    const cautionedVenues = new Set();
    // Optimization Item #8: Use pre-categorized features
    const seamsLayers = categorizedFeatures.seams;
    const seamsByStadium = new Map();

    // First pass: group events by stadium
    seamsLayers.forEach((layer) => {
        const props = layer.properties || {};
        const venue = props.VENUE || props.GAME_DETAIL_FACILITY_NAME || props.OBJECTID || 'unknown';
        const stadiumKey = venue.toString().trim().toLowerCase();

        if (!seamsByStadium.has(stadiumKey)) {
            seamsByStadium.set(stadiumKey, []);
        }

        seamsByStadium.get(stadiumKey).push({layer, props});
    });

    // Second pass: process each event
    seamsLayers.forEach((layer) => {
        const props = layer.properties || {};

        // GAME_DATE now represents TFR start time (epoch milliseconds)
        // END_DATE now represents TFR end time (epoch milliseconds)
        const gamedate = props.GAME_DATE;
        const enddate = props.END_DATE;

        const objectId = props.OBJECTID;
        const eventName = props.EVENT_NAME || "a scheduled event";
        const venue = props.VENUE || props.GAME_DETAIL_FACILITY_NAME || "an FAA-designated stadium";
        const stadiumKey = venue.toString().trim().toLowerCase();
        const stadiumEvents = seamsByStadium.get(stadiumKey) || [];

        // Convert epoch milliseconds to UTC using Luxon
        // GAME_DATE is the TFR start time
        const tfrStartDateTime = DateTime.fromMillis(gamedate, {zone: "utc"});
        // END_DATE is the TFR end time
        const tfrEndDateTime = DateTime.fromMillis(enddate, {zone: "utc"});

        if (gamedate && enddate) { // Check that both TFR times are available
            // Parse selected date and time as UTC
            const selectedDateTimeUTC = DateTime.fromFormat(
                `${selectedDate} ${selectedTime}`,
                "yyyy-MM-dd HH:mm",
                {zone: selectedTimezone}
            ).toUTC();

            const selectedEndDateTimeUTC = selectedDateTimeUTC.plus({minutes: duration});

            // Check if the selected operation time overlaps with the TFR period
            const isDnf = selectedDateTimeUTC <= tfrEndDateTime &&
                tfrStartDateTime <= selectedEndDateTimeUTC;

            const displayEvent = isDnf
                ? {
                    eventName,
                    venue,
                    gameDate: gamedate,
                    endDate: enddate,
                    gameDateDisplay: formatSeamsDateTime(gamedate),
                    endDateDisplay: formatSeamsDateTime(enddate)
                }
                : getSeamsDisplayEventInfo(stadiumEvents, selectedDate, selectedTime, selectedTimezone);

            const advisoryEventName = displayEvent?.eventName || eventName;
            const advisoryVenue = displayEvent?.venue || venue;
            const advisoryGameDateDisplay = displayEvent?.gameDateDisplay || formatSeamsDateTime(gamedate);
            const advisoryEndDateDisplay = displayEvent?.endDateDisplay || formatSeamsDateTime(enddate);

            if (isDnf) {
                return_layers.push({
                    layerId: "Stadium",
                    displayName: "Flight Restricted Airspace",
                    description: `Flight operations within 3 NM of an FAA-designated stadium or sporting venue (${advisoryVenue}) are restricted at this time for the event (${advisoryEventName}). Game Date: ${advisoryGameDateDisplay}; End Date: ${advisoryEndDateDisplay}. Please check SEAMS for information regarding when this restriction will be lifted.`,
                    status: "DNF",
                    flyzone_notice: `<p><a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/stadiums" target="_blank" rel="noopener noreferrer">FAA Resources</a> | <a href="https://services1.arcgis.com/n4Ot9Qz0t5espY4s/arcgis/rest/services/SEAMS_Production_View/FeatureServer" target="_blank" rel="noopener noreferrer">SEAMS Data Source</a></p>`
                });
            } else {
                if (!cautionedVenues.has(stadiumKey)) {
                    return_layers.push({
                        layerId: "Stadium",
                        displayName: "Flight Restricted Airspace",
                        description: `Flight operations within 3NM of an FAA-designated stadium or sporting venue (${advisoryVenue}) are restricted from 1 hour before the planned start time until 1 hour after the end of any qualifying event. Current advisory event: ${advisoryEventName}. Game Date: ${advisoryGameDateDisplay}; End Date: ${advisoryEndDateDisplay}. Check the stadium schedule found on SEAMS prior to any flight operations.`,
                        status: "CAUTION",
                        flyzone_notice: `<p><a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/stadiums" target="_blank" rel="noopener noreferrer">FAA Resources</a> | <a href="https://services1.arcgis.com/n4Ot9Qz0t5espY4s/arcgis/rest/services/SEAMS_Production_View/FeatureServer" target="_blank" rel="noopener noreferrer">SEAMS Data Source</a></p>`,
                    });
                    cautionedVenues.add(stadiumKey);
                }
            }
        } else {
            const displayEvent = getSeamsDisplayEventInfo(stadiumEvents, selectedDate, selectedTime, selectedTimezone);
            const advisoryEventName = displayEvent?.eventName || eventName;
            const advisoryVenue = displayEvent?.venue || venue;
            const advisoryGameDateDisplay = displayEvent?.gameDateDisplay || formatSeamsDateTime(gamedate);
            const advisoryEndDateDisplay = displayEvent?.endDateDisplay || formatSeamsDateTime(enddate);

            if (!cautionedVenues.has(stadiumKey)) {
                return_layers.push({
                    layerId: "Stadium",
                    displayName: "Flight Restricted Airspace",
                    description: `Flight operations within 3NM of an FAA-designated stadium or sporting venue (${advisoryVenue}) are restricted from 1 hour before the planned start time until 1 hour after the end of any qualifying event. Current advisory event: ${advisoryEventName}. Game Date: ${advisoryGameDateDisplay}; End Date: ${advisoryEndDateDisplay}. Check the stadium schedule found on SEAMS prior to any flight operations.`,
                    status: "CAUTION",
                    flyzone_notice: `<p><a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/stadiums" target="_blank" rel="noopener noreferrer">FAA Resources</a> | <a href="https://services1.arcgis.com/n4Ot9Qz0t5espY4s/arcgis/rest/services/SEAMS_Production_View/FeatureServer" target="_blank" rel="noopener noreferrer">SEAMS Data Source</a></p>`
                });
                cautionedVenues.add(stadiumKey);
            }
        }
    });

// end stadium layer


// end stadium layer


    //B4UFLY layers----------------------
    const processedLayers = new Set();

    // Optimization Item #8: Use pre-categorized features
    categorizedFeatures.b4ufly.forEach((layer, index) => {
        let b4ufly_layer_entry;
        const layerId = layer.layerId.toLowerCase();
        const props = layer.properties || {};

        // Define display name mapping
        const getDisplayName = (id) => {
            const nameMap = {
                'laanc-b4ufly-boundary-airspace': 'Flight Restricted Airspace',
                'laanc-b4ufly-faa-ria': 'FAA Recognized Identification Area (FRIA)',
                'laanc-b4ufly-recreational': 'Recreational Flyer Fixed Sites',
                'laanc-b4ufly-mtrs': 'Special Use Airspace',
                'laanc-b4ufly-fish-wildlife': 'Wilderness Area',
                'laanc-b4ufly-wilderness-forest': 'Wilderness Area',
                'laanc-b4ufly-wilderness-areas': 'Wilderness Area',
                'laanc-b4ufly-national-parks': 'National Park'
            };

            // Try exact match
            if (nameMap[id]) return nameMap[id];

            // Try with hyphens/underscores variations
            const altId1 = id.replace(/-/g, '_');
            const altId2 = id.replace(/_/g, '-');
            if (nameMap[altId1]) return nameMap[altId1];
            if (nameMap[altId2]) return nameMap[altId2];

            // Check for partial matches (this handles Mapbox suffixes like -cy0tz2)
            for (const [key, value] of Object.entries(nameMap)) {
                if (id.includes(key)) {
                    return value;
                }
            }

            // Fallback: format the ID nicely
            return id
                .replace('laanc-b4ufly-', '')
                .replace(/_/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };
           // part 93
        const displayName = getDisplayName(layerId);
        if (layerId.includes("laanc-b4ufly-boundary-airspace")) {
            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `<p>Operation overlaps with <strong>${props.NAME}</strong>, SECTOR -<strong>${props.SECTOR}</strong> and No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                              <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                status: "NOK",
                isBoundaryAirspace: true,
                flyzone_notice: `<p><a href="https://sua.faa.gov/sua/siteFrame.app"</p>`,
            };
        } else if (layerId.includes("national-parks")) {
            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `<p>Operation overlaps </strong>${props.UNIT_NAME}. Check with the National Park Service prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.nps.gov/articles/uncrewed-aircraft-in-the-national-parks.htm" target="_blank" rel="noopener noreferrer">National Park Services</a></p>`,
            };
        } else if (layerId.includes("laanc-b4ufly-faa-ria")) {
            const friaName = props.title || props.fria_name || "FRIA";
            const address = [
                props.address1,
                props.city,
                props.state,
                props.zipcode
            ].filter(Boolean).join(", ");

            let endDateUTC = "N/A";

            if (props.endDate !== undefined && props.endDate !== null) {
                const date = new Date(Number(props.endDate));
                if (!isNaN(date.getTime())) {
                    endDateUTC = date
                        .toUTCString()
                        .replace(/\d{2}:\d{2}:\d{2} GMT/, "")
                        .trim()
                        .toUpperCase()
                }
            }

            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `To operate without Remote ID, both the drone and the pilot must be located within the FRIA's boundaries throughout the operation.Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.`,
                status: "CAUTION",
                flyzone_notice: `<p><strong>FRIA Name:</strong> ${friaName}</p>
                                <p><strong>Address:</strong> ${address}</p>
                                <p><strong>End Date (UTC):</strong> ${endDateUTC}</p>`,

            };
        } else if (layerId.includes("laanc-b4ufly-recreational")) {
            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `Operation overlaps with <strong>${props.SITE_NAME}</strong>.Flight operations at a Recreational Flyer Fixed Site within controlled airspace must adhere to the site's operating limitations and safety guidelines, which are available from the fixed site sponsor..`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.faa.gov/uas/recreational_flyers" target="_blank" rel="noopener noreferrer">FAA Recreational Flyer Resources.</a> </p>
                                 <p><a href="https://www.modelaircraft.org/club-finder?city=Salina&state=KS&distance%5Bdistance%5D%5Bfrom%5D=17.02686&distance%5Bvalue%5D=38.7805555555556%2C-97.5788888888889" target="_blank" rel="noopener noreferrer">POC</a> </p>`,

            };
        } else if (layerId.includes("laanc-b4ufly-mtrs")) {

            // Optimization Item #10: Use local mtrRoutes instead of window.mtrRoutes
            // Add current IDENT to the set (avoid duplicates)
            if (props.IDENT) {
                mtrRoutes.add(props.IDENT);
            }

            // Convert all collected routes to comma separated string
            const allRoutes = Array.from(mtrRoutes).join(", ");

            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `<p> Operation overlaps with Military Training Route (MTRs) : <strong>${allRoutes}</strong>. This airspace may contain more air traffic or fast military aircraft.Maintain extra vigilance and awareness when flying.</p>
                              <p> Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://sua.faa.gov/sua/siteFrame.app" target="_blank" rel="noopener noreferrer">FAA MTRs Information</a></p>`,
            };
        }
        // fish-wildlife
        else if (layerId.includes("laanc-b4ufly-fish-wildlife")) {
            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `<p>Operation overlaps with <strong>${props.DESNAME}</strong>.Check with the Fish and Wildlife Service prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.fws.gov/" target="_blank" rel="noopener noreferrer">U.S. Fish & Wildlife Service</a></p>`,
            };
        }
        // forest-services
        else if (layerId.includes("laanc-b4ufly-wilderness-forest")) {

            const wildernessName = props?.wildernessname || "this area";

            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `<p>Operation overlaps with <strong>${wildernessName}</strong>.Check with the Forest Service prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.fs.usda.gov/" target="_blank" rel="noopener noreferrer">U.S. Forest Service</a></p>`,
            };
        }
        // wilderness
        else if (layerId.includes("laanc-b4ufly-wilderness-areas")) {
            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `<p>Operation overlaps with <strong>${props.NLCS_NAME}</strong>. Check with the Bureau of Land Management prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.wilderness.net/" target="_blank" rel="noopener noreferrer">Wilderness Information</a></p>`,
            };
        }
        // national-parks
        else if (layerId.includes("laanc-b4ufly-national-parks")) {
            b4ufly_layer_entry = {
                layerId: displayName,
                displayName: displayName,
                description: `<p>Operation overlaps </strong>${props.UNIT_NAME}. Check with the National Park Service prior to flight.Prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.nps.gov/articles/uncrewed-aircraft-in-the-national-parks.htm" target="_blank" rel="noopener noreferrer">National Park Services</a></p>`,
            };
        }


        //final array - check if we created an entry and it's not already processed
        if (b4ufly_layer_entry && !processedLayers.has(b4ufly_layer_entry.layerId)) {
            return_layers.push(b4ufly_layer_entry);
            processedLayers.add(b4ufly_layer_entry.layerId);
        }

    });


    // common time values
    // Extract hours and minutes
    const hours = selected_end_date_obj.getHours();
    const minutes = selected_end_date_obj.getMinutes();

    // Format the hours and minutes with leading zeros if necessary
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');

    // check for valid sunrise sunset time
    if (Object.keys(suntime).length !== 0) {
        // //console.log(suntime);
        // //console.log(`${formattedHours}:${formattedMinutes}`);
        let layer_title = "Night-time Operations";
        let description = '';
        let twilight_overlap_dawn = timeRangesOverlap(convertTo24Hour(suntime.dawn), convertTo24Hour(suntime.sunrise), selectedTime, `${formattedHours}:${formattedMinutes}`);
        let twilight_overlap_dusk = timeRangesOverlap(convertTo24Hour(suntime.sunset), convertTo24Hour(suntime.dusk), selectedTime, `${formattedHours}:${formattedMinutes}`);
        let night_overlap = timeRangesOverlap(convertTo24Hour(suntime.dusk), convertTo24Hour(suntime.dawn), selectedTime, `${formattedHours}:${formattedMinutes}`);

        if (night_overlap.length > 0) {
            layer_title = "Night Operations";
            if (operation_mode === 'Part 107') {
                description = `Nighttime flights may only be conducted by operators who have completed the required FAA training/testing. Nighttime flights require anti-collision lighting to be activated, visible for at least 3 statute miles, and have a flash rate sufficient to avoid a collision.`;
            } else {
                description = 'It is\n' +
                    'recommended that sUAS should be lit with\n' +
                    'anti-collision lighting visible for at least 3\n' +
                    'statute miles away and arrange lights on the\n' +
                    'UA in such a way that allows recreational\n' +
                    'flyers to determine the orientation and flight\n' +
                    'path of the aircraft. For additional guidance,\n' +
                    'refer to “Night Flight” in Advisory Circular\n' +
                    '91-57.';
            }
            //description += `Lights are required at night from ${suntime.dusk} to ${suntime.dawn}`;
        }

        // let civil_twilight_adddition_description = '';
        // if (operation_mode === 'Part 107')

        if ((night_overlap.length > 0 && twilight_overlap_dawn.length > 0)) {
            description += `Lights are required during civil twilight from ${suntime.dawn} to ${suntime.sunrise}.`;
        } else if ((night_overlap.length > 0 && twilight_overlap_dusk.length > 0)) {
            description += `Lights are required during civil twilight from ${suntime.sunset} to ${suntime.dusk}.`;
        } else if (twilight_overlap_dawn.length > 0) {
            layer_title = "Civil Twilight Operations";
            description += `Lights are required during civil twilight from ${suntime.dawn} to ${suntime.sunrise}. Flights require anti-collision lighting to be activated, visible for at least 3 statute miles, and have a flash rate sufficient to avoid a collision.`;
        } else if (twilight_overlap_dusk.length > 0) {
            layer_title = "Civil Twilight Operations";
            description += `Lights are required during civil twilight from ${suntime.sunset} to ${suntime.dusk}. Flights require anti-collision lighting to be activated, visible for at least 3 statute miles, and have a flash rate sufficient to avoid a collision.`;
        }

        if (description !== '') {
            return_layers.push({
                layerId: layer_title,
                description: description,
                status: "CAUTION",
                flyzone_notice: (operation_mode !== 'Part 107') ? `<a href="https://www.faa.gov/regulations_policies/advisory_circulars/index.cfm/go/document.information/documentID/1044036" target="_blank" rel="noopener noreferrer">Advisory Circular 91-57</a>` : ''
            });
        }

    }

    let tfr_layer_entry = {};
    // Optimization Item #8: Use pre-categorized features
    categorizedFeatures.tfr.forEach((layer, index) => {
        // console.log(layer.properties.notam_id);
        // console.log(`${selectedDate} ${selectedTime} ${selectedTimezone}`);
        // console.log(`${layer.properties.start_datetime} ${layer.properties.timezone}`);
        // console.log(`${layer.properties.end_datetime} ${layer.properties.timezone}`);
        // console.log(layer.properties.frequency);
        if ('frequency' in layer.properties && layer.properties.frequency !== null) { // daily occurence
            // TODO: fix the

            //if (isDateTimeInRange(`${selectedDate} ${selectedTime}`, layer.properties.start_datetime, layer.properties.end_datetime, selectedTimezone, layer.properties.timezone) || isDateTimeInRange(formatDateToYYYYMMDDHHMM(selected_end_date_obj), layer.properties.start_datetime, layer.properties.end_datetime, selectedTimezone, layer.properties.timezone)) {
            if (isDateTimeInRangeFrequency(`${selectedDate} ${selectedTime}`, duration, layer.properties.start_datetime, layer.properties.end_datetime, selectedTimezone, layer.properties.timezone)) {
                //console.log("tfr added");

                const timeRangeStr = layer.properties.time_range ? `daily ${layer.properties.time_range} ` : '';
                const timezoneStr = layer.properties.timezone ? layer.properties.timezone : 'UTC';

                return_layers.push({
                    layerId: "Temporary Flight Restriction (TFR)",
                    description: `<p>Operation overlaps with a TFR area. TFR will be in effect from ${layer.properties.start_datetime} to ${layer.properties.end_datetime}, ${timeRangeStr}${timezoneStr}.</p>
                                  <p>No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                                  <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                    status: "DNF",
                    isTFR: true,
                    flyzone_notice: `<a href="${layer.properties.tfr_link}" target="_blank" rel="noopener noreferrer">View TFR Details</a>`,
                });
            } else {
                const timeRangeStr = layer.properties.time_range ? `daily ${layer.properties.time_range} ` : '';
                const timezoneStr = layer.properties.timezone ? layer.properties.timezone : 'UTC';

                return_layers.push({
                    layerId: "Flight Restricted Airspace",
                    description: `<p>Operation overlaps with a Scheduled TFR area. TFR will be in effect from ${layer.properties.start_datetime} to ${layer.properties.end_datetime}, ${timeRangeStr}${timezoneStr}.</p>
                                  <p>No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                                  <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                    status: "CAUTION",
                    isTFR: false,
                    flyzone_notice: `<a href="${layer.properties.tfr_link}" target="_blank" rel="noopener noreferrer">View TFR Details</a>`,
                });

            }

        } else { // fixed range start to end
            // Handle missing start_datetime or end_datetime
            let tfrStartDateTime = layer.properties.start_datetime;
            let tfrEndDateTime = layer.properties.end_datetime;

            // If both are missing, show DNF without time check
            if (!tfrStartDateTime && !tfrEndDateTime) {
                return_layers.push({
                    layerId: "Temporary Flight Restriction (TFR)",
                    description: `<p>Operation overlaps with a TFR area.No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                                  <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                    status: "DNF",
                    isTFR: true,
                    flyzone_notice: `<a href="${layer.properties.tfr_link}" target="_blank" rel="noopener noreferrer">View TFR Details</a>`,
                });
            } else {
                // Handle case where start_datetime is undefined/null (treat as distant past)
                if (!tfrStartDateTime) {
                    tfrStartDateTime = "1970-01-01 00:00"; // Unix epoch as distant past
                }

                // Handle case where end_datetime is undefined/null (treat as distant future)
                if (!tfrEndDateTime) {
                    tfrEndDateTime = "2099-12-31 23:59"; // Distant future
                }

                // Get TFR timezone, default to UTC if not specified
                const tfrTimezone = layer.properties.timezone || "UTC";

                try {
                    // Now check for overlap with the guaranteed start and end times
                    if (isDateTimeInRange(`${selectedDate} ${selectedTime}`, duration, tfrStartDateTime, tfrEndDateTime, selectedTimezone, tfrTimezone)) {
                        const endTimeDisplay = layer.properties.end_datetime ?
                            `TFR will be in effect until ${layer.properties.end_datetime} ${tfrTimezone}.` :
                            `TFR is currently in effect.`;

                        return_layers.push({
                            layerId: "Temporary Flight Restriction (TFR)",
                            description: `<p>Operation overlaps with a TFR area. ${endTimeDisplay} No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                                          <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                            status: "DNF",
                            isTFR: true,
                            flyzone_notice: `<a href="${layer.properties.tfr_link}" target="_blank" rel="noopener noreferrer">View TFR Details</a>`,
                        });
                    } else {
                        const timeRangeStr = layer.properties.time_range ? `daily ${layer.properties.time_range} ` : '';
                        const timezoneStr = layer.properties.timezone ? layer.properties.timezone : 'UTC';

                        return_layers.push({
                            layerId: "Flight Restricted Airspace",
                            description: `<p>Operation overlaps with a Scheduled TFR area. TFR will be in effect from ${layer.properties.start_datetime} to ${layer.properties.end_datetime}, ${timeRangeStr}${timezoneStr}.</p>
                                      <p>No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                                      <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                            status: "CAUTION",
                            isTFR: false,
                            flyzone_notice: `<a href="${layer.properties.tfr_link}" target="_blank" rel="noopener noreferrer">View TFR Details</a>`,
                        });
                    }
                } catch (error) {
                    // If date parsing fails, treat as active TFR to be safe
                    return_layers.push({
                        layerId: "Temporary Flight Restriction (TFR)",
                        description: `<p>Operation overlaps with a TFR area. No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                                      <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                        status: "DNF",
                        isTFR: true,
                        flyzone_notice: `<a href="${layer.properties.tfr_link}" target="_blank" rel="noopener noreferrer">View TFR Details</a>`,
                    });
                }
            }
        }

    });
    //  End TFR

    // post processing ================================

    // remove duplicates (Optimization Item #7: Use custom hash instead of JSON.stringify)
    return_layers = [
        ...new Map(return_layers.map(item => [createLayerHashKey(item), item])).values()
    ];

    //console.log(schedule_time_array);

    // handle overlapping time ranges for scheduled airspaces
    // TODO: add TFR start end scenarios
    //if (Object.keys(schedule_time).length !== 0) {
    schedule_time_array.forEach((schedule_time, index) => {
        let temp_overlap = [];
        temp_overlap = timeRangesOverlap(schedule_time.startTime, schedule_time.endTime, selectedTime, `${formattedHours}:${formattedMinutes}`);

        // handle fulltime schedules
        if (schedule_time.startTime === '00:00' && schedule_time.endTime === '23:59' && temp_overlap.length === 2) {
            temp_overlap = [{
                overlapStart: temp_overlap[0].overlapStart,
                overlapEnd: temp_overlap[1].overlapEnd
            }];
        }
        // check for overlaps length
        if (temp_overlap.length > 0) {
            temp_overlap.forEach(overlap => {
                overlap.OBJECTID = schedule_time.OBJECTID;
            });
            //console.log(temp_overlap);
            auth_overlap_schedule.push(...temp_overlap);
        }
    });

    //console.log(auth_overlap_schedule);

    // let summary_obj = {}; // title value in the detail bar
    // let proceed = false;
    // if (return_layers.some(item => "DNF" === item.status)) {
    //     summary_obj = {title: 'Do Not Fly', status: "NOK"};
    //     // remove all other items from the return_layers
    //     return_layers = return_layers.filter(item => item.status === "DNF");
    //
    // } else if (return_layers.some(item => "NOK" === item.status)) {
    //     summary_obj = {title: 'Automatic Authorization Not Available', status: "NOK"};
    //     return_layers = return_layers.filter(item => item.status === "NOK");
    //
    // } else if (return_layers.some(item => "ALERT" === item.status)) {
    //     summary_obj = {
    //         title: 'Automatic Authorization Available',
    //         subtitle: 'Obtain automatic LAANC authorization to fly by securing airspace approval from the FAA.',
    //         status: 'ALERT'
    //     };
    //     //return_layers = return_layers.filter(item => item.status === "ALERT");
    //
    //     proceed = true;
    // } else if (return_layers.some(item => "CAUTION" === item.status)) {
    //     summary_obj = {
    //         title: 'Fly with Caution',
    //         subtitle: 'Review airspace restrictions and requirements before flying',
    //         status: 'ALERT'
    //     };
    //     proceed = true;
    // } else {
    //     summary_obj = {title: 'Clear To Fly', subtitle: 'Approval Not Required', status: "OK"};
    //     proceed = true;
    // }

    ////console.log(return_layers);
    ////console.log(summary_obj);

    // sort authorization specific layers


    // prepare the authorizations array
    /* Considerations -
    * 1. scheduled airspace
    * 2. multiple class d authorizing airports
    *
    *
    * Not to consider -
    * 1. current business logic set one height when multiple UASFM heights detected hence not considered when generating multiple
    * */

    // handle cases for scheduled airspaces
    let authorization_timestamps = []; // for user notifications
    //console.log("overlapped Schedule");
    //console.log(auth_overlap_schedule.length);
    if (auth_overlap_schedule.length > 0) {

        const groupedData = auth_overlap_schedule.reduce((acc, obj) => {
            const key = obj.OBJECTID; // The field you want to group by
            if (!acc[key]) {
                acc[key] = []; // Create a new array for this category if it doesn't exist
            }
            acc[key].push(obj); // Add the object to the array for that category
            return acc;
        }, {});

        // for (const OBJECTID in groupedData) {

        auth_overlap_schedule.forEach((schedule_item, index) => {
            let start_date = selectedDate;
            let end_date = selectedDate;

            // multiple overlaps
            if (index > 0) {
                // determine next day
                if (timeStringToMinutes(schedule_item.overlapStart) < timeStringToMinutes(auth_overlap_schedule[index - 1].overlapEnd)) {
                    start_date = addDayToDate(selectedDate);
                    end_date = addDayToDate(selectedDate);
                }
            } else if (timeStringToMinutes(schedule_item.overlapStart) > timeStringToMinutes(schedule_item.overlapEnd)) {
                end_date = addDayToDate(start_date);
            }

            // for converting yyyy-mm-dd hh:mm and timezone to UTC
            const dateTimeStr = `${start_date} ${schedule_item.overlapStart}`;
            // Create a DateTime object in the specified timezone
            const dateTimeInZone = DateTime.fromFormat(dateTimeStr, 'yyyy-MM-dd HH:mm', {zone: selectedTimezone});

            ////console.log(dateTimeInZone.toUTC().toISO());
            //console.log("adding to timestamps");
            //console.log({
            //     startTime: `${start_date} ${schedule_item.overlapStart}`,
            //     endTime: `${end_date} ${schedule_item.overlapEnd}`
            // });
            authorization_timestamps.push({
                startTime: `${start_date} ${schedule_item.overlapStart}`,
                endTime: `${end_date} ${schedule_item.overlapEnd}`,
                OBJECTID: schedule_item.OBJECTID
            });
            let authorization = {

                operationStartDateTime: dateTimeInZone.toUTC().toISO().replace('.000', ''),//reformatToISOString(`${start_date} ${schedule_item.overlapStart}`),
                //operationEndDateTime: reformatToISOString(`${start_date} ${schedule_item.overlapEnd}`),
                operationDuration: calculateDuration(schedule_item.overlapStart, schedule_item.overlapEnd),
                OBJECTID: schedule_item.OBJECTID
            };

            authorizations_time_schedule_container.push(authorization);
        });
        // }

        // auth_overlap_schedule.forEach((schedule_item, index) => {
        //
        //     let start_date = selectedDate;
        //     let end_date = selectedDate;
        //
        //     // multiple overlaps
        //     if (index > 0) {
        //         // determine next day
        //         if (timeStringToMinutes(schedule_item.overlapStart) < timeStringToMinutes(auth_overlap_schedule[index - 1].overlapEnd)) {
        //             start_date = addDayToDate(selectedDate);
        //             end_date = addDayToDate(selectedDate);
        //         }
        //     } else if (timeStringToMinutes(schedule_item.overlapStart) > timeStringToMinutes(schedule_item.overlapEnd)) {
        //         end_date = addDayToDate(start_date);
        //     }
        //
        //     // for converting yyyy-mm-dd hh:mm and timezone to UTC
        //     const dateTimeStr = `${start_date} ${schedule_item.overlapStart}`;
        //     // Create a DateTime object in the specified timezone
        //     const dateTimeInZone = DateTime.fromFormat(dateTimeStr, 'yyyy-MM-dd HH:mm', {zone: selectedTimezone});
        //
        //     ////console.log(dateTimeInZone.toUTC().toISO());
        //     //console.log("adding to timestamps");
        //     //console.log({
        //         startTime: `${start_date} ${schedule_item.overlapStart}`,
        //         endTime: `${end_date} ${schedule_item.overlapEnd}`
        //     });
        //     authorization_timestamps.push({
        //         startTime: `${start_date} ${schedule_item.overlapStart}`,
        //         endTime: `${end_date} ${schedule_item.overlapEnd}`,
        //         OBJECTID: schedule_item.OBJECTID
        //     });
        //     let authorization = {
        //
        //         operationStartDateTime: dateTimeInZone.toUTC().toISO().replace('.000', ''),//reformatToISOString(`${start_date} ${schedule_item.overlapStart}`),
        //         //operationEndDateTime: reformatToISOString(`${start_date} ${schedule_item.overlapEnd}`),
        //         operationDuration: calculateDuration(schedule_item.overlapStart, schedule_item.overlapEnd),
        //         OBJECTID: schedule_item.OBJECTID
        //     };
        //
        //     authorizations_time_schedule_container.push(authorization);
        // });
    } else { // handle regular scenarios

        // const dateTimeInZone = DateTime.fromFormat(formatDateToYYYYMMDDHHMM(selected_start_date_obj), 'yyyy-MM-dd HH:mm', { zone: selectedTimezone })
        // ////console.log(dateTimeInZone.toUTC().toISO());
        // authorization_timestamps.push({startTime: formatDateToYYYYMMDDHHMM(selected_start_date_obj), endTime: formatDateToYYYYMMDDHHMM(selected_end_date_obj)});
        //
        // let authorization = {
        //     operationStartDateTime: dateTimeInZone.toUTC().toISO().replace('.000', ''), //formatDateToISOStringWithoutTimezone(selected_start_date_obj),
        //     //operationEndDateTime: selected_end_date_obj,
        //     operationDuration: duration
        // };
        // authorizations_time_schedule_container.push(authorization);

        return_layers.push({
            layerId: "Uncontrolled Airspace",
            description: 'LAANC Authorization(s) not required',
            status: "OK",
        });

        return_layers = return_layers.filter(item => item.status !== "ALERT");
    }

    //console.log(authorizations_time_schedule_container);

    // authorizations based on class airspace
    const sorted_airspaces = sortAndCheckMatchWithFirst(auth_unique_class_airspace_touched);

    //console.log(airports.length);
    // console.log("sorted airspaces");
    //console.log({sorted_airspaces});

    // Optimization Item #9: Create Maps for O(1) lookups instead of O(n) find/filter in loops
    // This converts O(n²) to O(n) - 10-100x faster on large datasets
    const airportsByGlobalId = new Map(airports.map(airport => [airport.GLOBAL_ID, airport]));

    // Group auth_unique_uasfm_touched by composite key for fast filtering
    const uasfmByKey = new Map();
    auth_unique_uasfm_touched.forEach(item => {
        const key = `${item.APT_FAAID}|${item.APT_ICAO}|${item.AIRSPACE}`;
        if (!uasfmByKey.has(key)) {
            uasfmByKey.set(key, []);
        }
        uasfmByKey.get(key).push(item);
    });

    // Group authorizations by OBJECTID for O(1) lookup
    const authorizationsByObjectId = new Map();
    authorizations_time_schedule_container.forEach((auth, index) => {
        if (!authorizationsByObjectId.has(auth.OBJECTID)) {
            authorizationsByObjectId.set(auth.OBJECTID, []);
        }
        authorizationsByObjectId.get(auth.OBJECTID).push({auth, index});
    });

    let operation = {error: {}, authorizations: [], authorization_timestamps: []};
    sorted_airspaces.sortedList.forEach((matchingAirspace, matching_airspace_index) => { // sorted_airspaces.matchingItems.forEach((matchingAirspace, matching_airspace_index)

        // add facility id to the authorization (Optimization Item #9: Map lookup instead of find)
        const result = airportsByGlobalId.get(matchingAirspace.ADHP_ID);

        //console.log("obj => obj.GLOBAL_ID === matchingAirspace.ADHP_ID");
        //console.log(result);

        // reduced
        let class_layer = "NA";
        if (matchingAirspace.CLASS === 'B') {
            class_layer = "laanc-airspace-class-b";
        } else if (matchingAirspace.CLASS === 'C') {
            class_layer = "laanc-airspace-class-c";
        } else if (matchingAirspace.CLASS === 'D') {
            class_layer = "laanc-airspace-class-d";
        } else if (matchingAirspace.CLASS === 'E') {
            class_layer = "laanc-airspace-class-e";
        } else {
            //console.log("unrecognized airspace class");
        }

        //console.log(result);
        //console.log(class_layer);

        if (result && class_layer !== "NA") {

            //console.log(auth_unique_uasfm_touched);

            // determine facility ids match with the UASFM grids
            // Optimization Item #9: Use Map lookup instead of filter (O(1) vs O(n))
            const lookupKey = `${result.IDENT}|${result.ICAO_ID}|${matchingAirspace.CLASS}`;
            const matchingUasfm = uasfmByKey.get(lookupKey) || [];
            let facility_map_class = Array.from(new Set(matchingUasfm.map(item => item.AIRSPACE)));

            //console.log(facility_map_class);
            // console.log(airspace_class_codes);

            // check matching facility map and matching airspace class codes for IDENT and ICAO_ID
            if (facility_map_class.length > 0) { // && airspace_class_codes.length > 0
                // Optimization Item #9: Use Map lookup instead of forEach with conditional (O(1) vs O(n))
                const matchingAuthorizations = authorizationsByObjectId.get(matchingAirspace.OBJECTID) || [];

                matchingAuthorizations.forEach(({auth: authorization, index: time_index}) => {
                    let copy_authorization = {...authorization};

                    //console.log(copy_authorization.OBJECTID);
                    //console.log(matchingAirspace.CLASS);
                    //console.log(auth_unique_uasfm_touched);

                    let authorization_geojson_area = getGeoIntersection(mapref, selectedArea_circle, class_layer, matchingAirspace.OBJECTID);//class_layer, matchingAirspace.OBJECTID

                    // Validate the geometry result before proceeding
                    if (!authorization_geojson_area || !authorization_geojson_area.features || !authorization_geojson_area.features[0]) {
                        return;
                    }

                    // Ensure geometry type is Polygon (not MultiPolygon)
                    const geometryType = authorization_geojson_area.features[0].geometry.type;
                    if (geometryType !== 'Polygon') {
                        return;
                    }

                    // console.log(authorization_geojson_area);

                    const local_facility = result.IDENT;


                    //const local_facility_map = JSON.parse(JSON.stringify(facility_map)); // object array
                    const local_airspace_class_codes = JSON.parse(JSON.stringify(facility_map_class)); // string array

                    copy_authorization.facility = local_facility;
                    //copy_authorization.facilityMaps = local_facility_map;
                    copy_authorization.airspaceClassCodes = local_airspace_class_codes;

                    delete copy_authorization.OBJECTID; // remove the OBJECTID contained in the authorization_schedule object
                    authorization_geojson_area.features[0].properties = {...authorization_geojson_area.features[0].properties, ...copy_authorization};
                    //console.log(copy_authorization);

                    operation.authorizations.push(authorization_geojson_area);
                    operation.authorization_timestamps.push(authorization_timestamps[time_index]);
                });

            } else {
                operation.error.message = 'UASFM/Class airspaces not found';
            }
        } else {
            operation.error.message = 'Matching facility not found';
        }
    });

    // ================= REMOVE DUPLICATES ================= (Optimization Item #7)
    return_layers = [
        ...new Map(return_layers.map(item => [createLayerHashKey(item), item])).values()
    ];

// ================= ADD NON-INTERSECTING PROXIMITY LAYERS =================
// Add layers that are within 2NM but NOT intersecting the operation area
    const nonIntersectingProximityLayers = proximity2NMResults.filter(p => !p.intersectsOperation);

// Deduplicate proximity layers: keep only the closest one for each layer type + facility
    const proximityLayerMap = new Map();

    for (const proximityLayer of nonIntersectingProximityLayers) {
        const props = proximityLayer.properties || {};
        const layerId = proximityLayer.layerId;


        // Create unique key based on layer type and facility/location
        let uniqueKey = layerId;
        if (layerId === 'seams') {
            // For SEAMS, group by facility name
            uniqueKey = `seams_${props.GAME_DETAIL_FACILITY_NAME || props.FACILITY_NAME || 'unknown'}`;
        } else if (layerId === 'tfr') {
            // For TFR, group by NOTAM ID
            uniqueKey = `tfr_${props.notam_id || 'unknown'}`;
        } else {
            // For other layers, group by name or base
            const facilityName = props.NAME || props.Base || props.Facility || props.UNIT_NAME || 'unknown';
            uniqueKey = `${layerId}_${facilityName}`;
        }

        // Keep only the closest one for each unique key
        if (!proximityLayerMap.has(uniqueKey) || proximityLayer.distanceNM < proximityLayerMap.get(uniqueKey).distanceNM) {
            proximityLayerMap.set(uniqueKey, proximityLayer);
        }
    }

    for (const proximityLayer of proximityLayerMap.values()) {
        const props = proximityLayer.properties || {};
        const layerId = proximityLayer.layerId;
        // ONLY for B4UFLY layers
        const normalizedLayerId = (layerId || "").toLowerCase();
        let layerEntry = null;

        // Build the same format as intersecting layers based on layer type
        if (layerId === "laanc-specialuse") {
            const typeCode = props.TYPE_CODE;
            if (typeCode === "P" || typeCode === "R") {
                const type = (typeCode === "P") ? 'Prohibited' : 'Restricted';
                layerEntry = {
                    layerId: `${type} Special Use Airspace (SUA)`,
                    displayName: "Flight Restricted Airspace",
                    description: `<p>Operation is ${proximityLayer.distanceNM} NM from ${props.NAME} SUA. No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                              <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                    status: "DNF",
                    flyzone_notice: `<a href="https://sua.faa.gov/" target="_blank" rel="noopener noreferrer">https://sua.faa.gov/</a>`,
                    distanceNM: proximityLayer.distanceNM,
                    isProximityOnly: true
                };
            } else if (typeCode === "A" || typeCode === "MOA" || typeCode === "W") {
                let type = "";
                if (typeCode === "A") type = "Alert Area";
                if (typeCode === "MOA") type = "Military Training Area";
                if (typeCode === "W") type = "Warning Area";

                layerEntry = {
                    layerId: `${type} Special Use Airspace (SUA)`,
                    displayName: "Special Use Airspace",
                    description: `<p>Operation is ${proximityLayer.distanceNM} NM from ${props.NAME} SUA (${type}).</p>
                              <p>This airspace may contain more air traffic or fast military aircraft. Maintain extra vigilance and awareness when flying.</p>
                              <p>Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`,
                    status: "CAUTION",
                    flyzone_notice: `<a href="https://sua.faa.gov/" target="_blank" rel="noopener noreferrer">https://sua.faa.gov/</a>`,
                    distanceNM: proximityLayer.distanceNM,
                    isProximityOnly: true
                };
            }
        } else if (layerId === "laanc-dc") {
            layerEntry = {
                layerId: "Drone Operations Restricted Area",
                displayName: "Flight Restricted Airspace",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from DC FRZ. No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                          <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                status: "DNF",
                flyzone_notice: `<a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/dc" target="_blank" rel="noopener noreferrer">View FAA Resource</a>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (layerId === "laanc-nsufr") {
            layerEntry = {
                layerId: "National Security Restrictions (NSUFR)",
                displayName: "Flight Restricted Airspace",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from ${(props.Base?.trim() || props.Facility)}. No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                          <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                status: "DNF",
                flyzone_notice: `The POC for this airspace is ${props.POC}`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (layerId === "laanc-pnsufr") {
            // Part-Time NSUFR proximity layer - check if active during operation time
            // Compute operation start and end times
            const [sel_year, sel_month, sel_day] = selectedDate.split('-').map(Number);
            const [sel_hours, sel_minutes] = selectedTime.split(':').map(Number);
            const operationStartDate = new Date(sel_year, sel_month - 1, sel_day, sel_hours, sel_minutes);
            const operationEndDate = addMinutesToDate(operationStartDate, duration);

            const operationStartStr = formatDateToYYYYMMDDHHMM(operationStartDate);
            const operationEndStr = formatDateToYYYYMMDDHHMM(operationEndDate);

            // Check if PNSUFR is active during operation time using ACTIVETIME and ENDTIME
            let isPnsufrActive = false;
            try {
                isPnsufrActive = isEpochOverlap(
                    operationStartStr,
                    operationEndStr,
                    selectedTimezone,
                    props.ACTIVETIME,
                    props.ENDTIME
                );
            } catch (e) {
                // If time check fails, default to inactive (no warning)
                isPnsufrActive = false;
            }

            // ONLY show warning if the pnsufr is ACTIVE during operation time
            if (isPnsufrActive) {
                layerEntry = {
                    layerId: "National Security Restrictions (NSUFR)",
                    displayName: "Flight Restricted Airspace",
                    description: `<p>Operation is ${proximityLayer.distanceNM} NM from an active part-time National Security UAS Flight Restriction at ${props.Base}. No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>`,
                    status: "DNF",
                    flyzone_notice: `The POC for this airspace is ${props.POC}`,
                    distanceNM: proximityLayer.distanceNM,
                    isProximityOnly: true
                };
            }
        } else if (layerId === "tfr") {
            // TFR proximity layer - check if TFR is active during operation time
            let tfrStatus = "CAUTION"; // Default to caution if not active
            let tfrDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from a TFR area.`;

            // Check if TFR is active during the operation time
            let isTfrActive = false;
            let isTfrExpired = false;

            if ('frequency' in props && props.frequency !== null) {
                // Recurring/daily TFR
                try {
                    isTfrActive = isDateTimeInRangeFrequency(
                        `${selectedDate} ${selectedTime}`,
                        duration,
                        props.start_datetime,
                        props.end_datetime,
                        selectedTimezone,
                        props.timezone
                    );
                } catch (e) {
                    // If time check fails, default to inactive (CAUTION)
                    isTfrActive = false;
                }

                // Check if recurring TFR has expired (end date is before selected operation time)
                if (props.end_datetime) {
                    try {
                        const operationStartDate = DateTime.fromFormat(`${selectedDate} ${selectedTime}`, "yyyy-MM-dd HH:mm", {zone: selectedTimezone});
                        const tfrTimezone = props.timezone || "UTC";
                        const tfrZone = TFR_TIMEZONE_MAP[tfrTimezone] || "UTC";
                        const tfrEnd = DateTime.fromFormat(props.end_datetime, "yyyy-MM-dd HH:mm", {zone: tfrZone});

                        // TFR is expired if the end date is before the operation starts
                        isTfrExpired = tfrEnd.toUTC() < operationStartDate.toUTC();
                    } catch (e) {
                        // If parsing fails, assume not expired
                        isTfrExpired = false;
                    }
                }

                if (isTfrActive) {
                    const timeRangeStr = props.time_range ? `daily ${props.time_range} ` : '';
                    const timezoneStr = props.timezone ? props.timezone : 'UTC';
                    tfrDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from an active TFR area. TFR will be in effect from ${props.start_datetime} to ${props.end_datetime}, ${timeRangeStr}${timezoneStr}.</p>
                                  <p>Operating a small unmanned aircraft in restricted airspace is prohibited without permission from the appropriate authority.</p>`;
                } else if (!isTfrExpired) {
                    // TFR is scheduled for the future (not active now, but not expired)
                    const timeRangeStr = props.time_range ? `daily ${props.time_range} ` : '';
                    const timezoneStr = props.timezone ? props.timezone : 'UTC';
                    tfrDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from a scheduled TFR area. TFR will be in effect from ${props.start_datetime} to ${props.end_datetime}, ${timeRangeStr}${timezoneStr}.</p>
                                  <p>Ensure TFR is not active during your operation time.</p>`;
                }
            } else {
                // Fixed date range TFR
                let tfrStartDateTime = props.start_datetime;
                let tfrEndDateTime = props.end_datetime;

                // If both dates are missing, treat as active and not expired (permanent TFR)
                if (!tfrStartDateTime && !tfrEndDateTime) {
                    isTfrActive = true;
                    isTfrExpired = false;
                    tfrDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from a TFR area. Operating a small unmanned aircraft in restricted airspace is prohibited without permission from the appropriate authority.</p>`;
                } else {
                    // Handle missing dates with defaults
                    if (!tfrStartDateTime) {
                        tfrStartDateTime = "1970-01-01 00:00";
                    }
                    if (!tfrEndDateTime) {
                        tfrEndDateTime = "2099-12-31 23:59";
                    }

                    // Get TFR timezone, default to UTC if not specified
                    const tfrTimezoneAbbr = props.timezone || "UTC";
                    const tfrTimezone = TFR_TIMEZONE_MAP[tfrTimezoneAbbr] || tfrTimezoneAbbr;

                    try {
                        isTfrActive = isDateTimeInRange(
                            `${selectedDate} ${selectedTime}`,
                            duration,
                            tfrStartDateTime,
                            tfrEndDateTime,
                            selectedTimezone,
                            tfrTimezoneAbbr
                        );
                    } catch (e) {
                        // If time check fails, treat as active to be safe for proximity warnings
                        isTfrActive = true;
                    }

                    // Check if TFR has expired (ended before the selected operation time)
                    try {
                        const operationStartDate = DateTime.fromFormat(`${selectedDate} ${selectedTime}`, "yyyy-MM-dd HH:mm", {zone: selectedTimezone});
                        const tfrEnd = DateTime.fromFormat(tfrEndDateTime, "yyyy-MM-dd HH:mm", {zone: tfrTimezone});

                        // TFR is expired if it ended before the operation starts
                        isTfrExpired = tfrEnd.toUTC() < operationStartDate.toUTC();
                    } catch (e) {
                        // If parsing fails, assume not expired (safer)
                        isTfrExpired = false;
                    }

                    if (isTfrActive) {
                        const endTimeDisplay = props.end_datetime ?
                            `TFR will be in effect until ${props.end_datetime} ${tfrTimezoneAbbr}.` :
                            `TFR is currently in effect.`;
                        tfrDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from an active TFR area. ${endTimeDisplay} Operating a small unmanned aircraft in restricted airspace is prohibited without permission from the appropriate authority.</p>`;
                    } else if (!isTfrExpired) {
                        // TFR is scheduled for the future (not active now, but not expired)
                        tfrDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from a scheduled TFR area. TFR will be in effect from ${props.start_datetime || 'start date'} to ${props.end_datetime || 'end date'} ${tfrTimezoneAbbr}.</p>
                                      <p>Ensure TFR is not active during your operation time.</p>`;
                    }
                }
            }

            tfrStatus = isTfrActive ? "DNF" : "CAUTION";
            tfrDescription += `<p>Violations of these restrictions may result in action by the FAA and/or law enforcement.</p>`;

            // Only show TFR if it's active or scheduled (not expired)
            if (isTfrActive || !isTfrExpired) {
                layerEntry = {
                    layerId: "Temporary Flight Restriction (TFR)",
                    displayName: "Temporary Flight Restriction",
                    description: tfrDescription,
                    status: tfrStatus,
                    flyzone_notice: props.tfr_link ? `<a href="${props.tfr_link}" target="_blank" rel="noopener noreferrer">View TFR Details</a>` : '',
                    distanceNM: proximityLayer.distanceNM,
                    isProximityOnly: true
                };
            }
        } else if (layerId === "seams") {
            // SEAMS proximity layer - check if event is active during operation time
            let seamsStatus = "CAUTION";
            let seamsDescription = "";

            const gamedate = props.GAME_DATE;
            const enddate = props.END_DATE;
            const eventName = props.EVENT_NAME || "a scheduled event";
            const venue = props.VENUE || props.GAME_DETAIL_FACILITY_NAME || "a stadium";

            if (gamedate && enddate) {
                // Convert epoch milliseconds to UTC using Luxon
                const tfrStartDateTime = DateTime.fromMillis(gamedate, {zone: "utc"});
                const tfrEndDateTime = DateTime.fromMillis(enddate, {zone: "utc"});

                // Parse selected date and time as UTC
                const selectedDateTimeUTC = DateTime.fromFormat(
                    `${selectedDate} ${selectedTime}`,
                    "yyyy-MM-dd HH:mm",
                    {zone: selectedTimezone}
                ).toUTC();

                const selectedEndDateTimeUTC = selectedDateTimeUTC.plus({minutes: duration});

                // Check if the selected operation time overlaps with the TFR period
                const isActive = selectedDateTimeUTC <= tfrEndDateTime &&
                    tfrStartDateTime <= selectedEndDateTimeUTC;

                if (isActive) {
                    seamsStatus = "DNF";
                    seamsDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from an active stadium TFR at ${venue} for ${eventName}. Stadium TFRs prohibit drone operations within 3 NM from 1 hour before until 1 hour after the event.</p>
                                      <p>Event Date: ${formatSeamsDateTime(gamedate)}; End Date: ${formatSeamsDateTime(enddate)}.</p>`;
                } else {
                    seamsStatus = "CAUTION";
                    seamsDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from ${venue}. Stadium TFRs prohibit drone operations within 3 NM when events are taking place.</p>
                                      <p>Upcoming event: ${eventName}. Event Date: ${formatSeamsDateTime(gamedate)}; End Date: ${formatSeamsDateTime(enddate)}.</p>
                                      <p>Check event schedules before flying in this area.</p>`;
                }
            } else {
                // No date information available
                seamsDescription = `<p>Operation is ${proximityLayer.distanceNM} NM from ${venue}. Stadium TFRs prohibit drone operations within 3 NM when events are taking place.</p>
                              <p>Check event schedules before flying in this area.</p>`;
            }

            layerEntry = {
                layerId: "Stadium TFR (SEAMS)",
                displayName: "Stadium Flight Restriction",
                description: seamsDescription,
                status: seamsStatus,
                flyzone_notice: `<a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/stadiums" target="_blank" rel="noopener noreferrer">SEAMS TFR</a> | <a href="https://services1.arcgis.com/n4Ot9Qz0t5espY4s/arcgis/rest/services/SEAMS_Production_View/FeatureServer" target="_blank" rel="noopener noreferrer">SEAMS Data Source</a>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (layerId === "dc-sfra") {
            // DC SFRA proximity layer (nearby but not intersecting)
            layerEntry = {
                layerId: "Special Flight Rules Area (SFRA)",
                displayName: "Special Flight Rules Area",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from DC Special Flight Rules Area (SFRA). Flying a drone for recreational or nonrecreational use between 15 and 30 miles from Washington, D.C. is allowed under specific operating conditions.</p>
                          <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                status: "CAUTION",
                flyzone_notice: `<a href="https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/dc" target="_blank" rel="noopener noreferrer">FAA Resource</a>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };

            // ================= B4UFLY LAYERS ONLY =================

        } else if (normalizedLayerId.includes("b4ufly-boundary-airspace")) {
            // Part 93 Special Air
            layerEntry = {
                layerId: "Flight Restricted Airspace",
                displayName: "Flight Restricted Airspace",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from <strong>${props.NAME}</strong>, SECTOR -<strong>${props.SECTOR}</strong> and No person may operate a small unmanned aircraft in restricted airspace unless that person has permission from the appropriate authority.</p>
                              <p>Individuals violating any of these restrictions may be subject to FAA and/or law enforcement action.</p>`,
                status: "NOK",
                flyzone_notice: `<p><a href="https://sua.faa.gov/sua/siteFrame.app"</p>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (normalizedLayerId.includes("laanc-b4ufly-national-parks")) {

            //national parks
            layerEntry = {
                layerId: "National Park",
                displayName: "National Park",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from <strong>${props.UNIT_NAME}</strong>. Check with the National Park Service prior to flight.Prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.nps.gov/articles/uncrewed-aircraft-in-the-national-parks.htm" target="_blank" rel="noopener noreferrer">National Park Services</a></p>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (normalizedLayerId.includes("b4ufly-fria")) {
            //faa-ria
            const friaName = props.title || props.fria_name || "FRIA";
            const address = [
                props.address1,
                props.city,
                props.state,
                props.zipcode
            ].filter(Boolean).join(", ");

            let endDateUTC = "N/A";

            if (props.endDate !== undefined && props.endDate !== null) {
                const date = new Date(Number(props.endDate));
                if (!isNaN(date.getTime())) {
                    endDateUTC = date
                        .toUTCString()
                        .replace(/\d{2}:\d{2}:\d{2} GMT/, "")
                        .trim()
                        .toUpperCase()
                }
            }
            layerEntry = {
                layerId: "FAA Recognized Identification Area (FRIA)",
                displayName: "FAA Recognized Identification Area (FRIA)",
                description: `Operation is ${proximityLayer.distanceNM} NM from FRIA.To operate without Remote ID, both the drone and the pilot must be located within the FRIA's boundaries throughout the operation.Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.`,
                status: "CAUTION",
                flyzone_notice: `<p><strong>FRIA Name:</strong> ${friaName}</p>
                                <p><strong>Address:</strong> ${address}</p>
                                <p><strong>End Date (UTC):</strong> ${endDateUTC}</p>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };


        } else if (normalizedLayerId.includes("b4ufly-recreational")) {
            //recreational
            layerEntry = {
                layerId: "Recreational Flyer Fixed Sites",
                displayName: "Recreational Flyer Fixed Sites",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from <strong>${props.SITE_NAME || props.Base || 'a Recreational Flyer Fixed Site'}</strong>. Flight operations at a Recreational Flyer Fixed Site within controlled airspace must adhere to the site's operating limitations and safety guidelines.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.faa.gov/uas/recreational_flyers" target="_blank" rel="noopener noreferrer">FAA Recreational Flyer Resources</a></p>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (normalizedLayerId.includes("b4ufly-mtrs")) {
            //mtrs

            // Optimization Item #10: Use local mtrRoutes instead of window.mtrRoutes
            // Add current IDENT to the set (avoid duplicates)
            if (props.IDENT) {
                mtrRoutes.add(props.IDENT);
            }

            // Convert all collected routes to comma separated string
            const allRoutes = Array.from(mtrRoutes).join(", ");

            layerEntry = {
                layerId: "Special Use Airspace",
                displayName: "Special Use Airspace",
                description: `<p> Operation is ${proximityLayer.distanceNM} NM from Military Training Route (MTRs) : <strong>${allRoutes}</strong>. This airspace may contain more air traffic or fast military aircraft.Maintain extra vigilance and awareness when flying.</p>
                              <p> Prior to flight, the remote pilot in command must assess local airspace and any flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://sua.faa.gov/sua/siteFrame.app" target="_blank" rel="noopener noreferrer">FAA MTRs Information</a></p>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (normalizedLayerId.includes("laanc-b4ufly-fish-wildlife")) {
            //fish-wildlife
            layerEntry = {
                layerId: "Wilderness Area",
                displayName: "Wilderness Area",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from <strong>${props.DESNAME}</strong>.Check with the Fish and Wildlife Service prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.fws.gov/" target="_blank" rel="noopener noreferrer">U.S. Fish & Wildlife Service</a></p>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (normalizedLayerId.includes("laanc-b4ufly-wilderness-forest")) {
            //wilderness-forest

            const wildernessName = props?.wildernessname || "this area";
            layerEntry = {
                layerId: "Wilderness Area",
                displayName: "Wilderness Area",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from <strong>${wildernessName}</strong>.Check with the Forest Service prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.fs.usda.gov/" target="_blank" rel="noopener noreferrer">U.S. Forest Service</a></p>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        } else if (normalizedLayerId.includes("laanc-b4ufly-wilderness-areas")) {
            //wilderness-areas
            layerEntry = {
                layerId: "Wilderness Area",
                displayName: "Wilderness Area",
                description: `<p>Operation is ${proximityLayer.distanceNM} NM from <strong>${props.NLCS_NAME}</strong>. Check with the Bureau of Land Management prior to flight.</p>
                              <p>The remote pilot in command must assess the local airspace along with any other takeoff,landing, and flight restrictions.</p>`,
                status: "CAUTION",
                flyzone_notice: `<p><a href="https://www.wilderness.net/" target="_blank" rel="noopener noreferrer">Wilderness Information</a></p>`,
                distanceNM: proximityLayer.distanceNM,
                isProximityOnly: true
            };
        }

        if (layerEntry) {
            return_layers.push(layerEntry);
        }
    }

// ================= SAVE COPIES =================
    let allLayers = [...return_layers];
    let return_layers_second = [...return_layers];

// ================= SEPARATE PROXIMITY-ONLY FROM INTERSECTING LAYERS =================
// Proximity-only layers should NOT affect authorization decisions
    const intersectingLayers = return_layers.filter(x => !x.isProximityOnly);
    const proximityOnlyLayers_temp = return_layers.filter(x => x.isProximityOnly === true);

// ================= VARIABLES =================
    let summary_obj = {};
    let proceed = false;
    let highestPriorityLayer = null;

// ================= CHECK FOR EACH STATUS IN PRIORITY ORDER =================
// IMPORTANT: Only check intersecting layers, NOT proximity-only layers
    const hasTFR = intersectingLayers.some(x => x.status === "DNF" && x.isTFR === true);
    const hasDNF = intersectingLayers.some(x => x.status === "DNF" && !x.isTFR);
    const hasNOK = intersectingLayers.some(x => x.status === "NOK" && !x.isBoundaryAirspace);
    const hasCAUTION = intersectingLayers.some(x => x.status === "CAUTION");
    const hasALERT = intersectingLayers.some(x => x.status === "ALERT");
    const hasOK = intersectingLayers.some(x => x.status === "OK");

// ================= CHECK IF ANY RESTRICTION EXISTS =================
    const hasAnyRestriction = hasTFR || hasDNF || hasNOK || hasALERT || hasCAUTION;
    
    // part 93 (allow submit button if only part 93 SATA boundary airspace is present and no other restrictions exist)
    const hasBoundaryAirspaceOnly =
    intersectingLayers.some(x => x.isBoundaryAirspace === true) &&
    !intersectingLayers.some(x => x.status === "DNF") &&
    !intersectingLayers.some(x => x.status === "NOK" && x.isBoundaryAirspace !== true);

if (hasBoundaryAirspaceOnly) {
    summary_obj = {
        title: 'Fly with Caution',
        subtitle: 'Boundary airspace advisory only; no other NOK/DNF restrictions found',
        status: 'ALERT'
    };
    highestPriorityLayer = intersectingLayers.find(x => x.isBoundaryAirspace === true);
    proceed = true;
}

// ================= SUMMARY BASED ON PRIORITY =================
// Priority for SUMMARY: TFR > DNF > NOK > ALERT > CAUTION > OK
// OK summary appears ONLY if NO other status exists

    if (hasTFR) {
        summary_obj = {
            title: 'Temporary Flight Restriction - Do Not Fly',
            status: 'NOK'
        };
        highestPriorityLayer = intersectingLayers.find(x => x.status === "DNF" && x.isTFR === true);
        proceed = false;

    } else if (hasDNF) {
        summary_obj = {
            title: 'Do Not Fly - Airspace Restricted',
            status: 'NOK'
        };
        highestPriorityLayer = intersectingLayers.find(x => x.status === "DNF" && !x.isTFR);
        proceed = false;

    } else if (hasNOK) {
        summary_obj = {
            title: 'Automatic Authorization Not Available',
            status: 'NOK'
        };
        highestPriorityLayer = intersectingLayers.find(x => x.status === "NOK");
        proceed = false;

    } else if (hasALERT) {
        summary_obj = {
            title: 'Automatic Authorization Available',
            subtitle: 'Obtain automatic LAANC authorization to fly',
            status: 'ALERT'
        };
        highestPriorityLayer = intersectingLayers.find(x => x.status === "ALERT");
        proceed = true;

    } else if (hasCAUTION) {
        summary_obj = {
            title: 'Fly with Caution',
            subtitle: 'Review airspace restrictions before flying',
            status: 'ALERT'
        };
        highestPriorityLayer = intersectingLayers.find(x => x.status === "CAUTION");
        proceed = true;


    } else {
        // OK - ONLY appears when NO TFR, DNF, NOK, CAUTION, ALERT exists
        summary_obj = {
            title: 'Clear To Fly',
            subtitle: 'Approval Not Required',
            status: 'OK'
        };
        highestPriorityLayer = intersectingLayers.find(x => x.status === "OK");
        proceed = true;
    }

// ================= REMOVE UNCONTROLLED AIRSPACE IF RESTRICTIONS EXIST =================
    if (hasAnyRestriction) {
        allLayers = allLayers.filter(x => x.layerId !== "Uncontrolled Airspace");
    }

// ================= FILTER OK LAYERS IF ANY RESTRICTION EXISTS =================
// If there's any restriction (TFR, DNF, NOK, CAUTION, ALERT), remove OK layers
    let filteredLayers = [...allLayers];

    if (hasAnyRestriction) {
        // Remove OK layers when any restriction exists
        filteredLayers = filteredLayers.filter(x => x.status !== "OK");
    }

// ================= SEPARATE PROXIMITY-ONLY LAYERS =================
// Separate layers that are proximity-only (within 2NM but not intersecting)
    const proximityOnlyLayers = filteredLayers.filter(x => x.isProximityOnly === true);
    const mainLayers = filteredLayers.filter(x => !x.isProximityOnly);

// ================= ORDER MAIN LAYERS BY PRIORITY =================
// Priority order for DISPLAY: TFR, DNF, NOK, CAUTION, ALERT, OK

    const tfrLayers = mainLayers.filter(x => x.status === "DNF" && x.isTFR === true);
    const dnfLayers = mainLayers.filter(x => x.status === "DNF" && !x.isTFR);
    const nokLayers = mainLayers.filter(x => x.status === "NOK");
    const cautionLayers = mainLayers.filter(x => x.status === "CAUTION");
    const alertLayers = mainLayers.filter(x => x.status === "ALERT");
    const okLayers = mainLayers.filter(x => x.status === "OK");

// Create ordered layers according to priority (without proximity-only layers)
    let orderedLayers = [
        ...tfrLayers,      // 1. TFR (highest priority)
        ...dnfLayers,      // 2. DNF
        ...nokLayers,      // 3. NOK
        ...cautionLayers,  // 4. CAUTION
        ...alertLayers,    // 5. ALERT
        ...okLayers        // 6. OK (lowest priority) - only included if no restrictions
    ];

// ================= ENSURE HIGHEST PRIORITY LAYER IS AT TOP =================
    if (highestPriorityLayer) {
        const index = orderedLayers.findIndex(layer =>
            JSON.stringify(layer) === JSON.stringify(highestPriorityLayer)
        );

        if (index !== -1 && index !== 0) {
            const [priorityLayer] = orderedLayers.splice(index, 1);
            orderedLayers.unshift(priorityLayer);
        }
    }

// ================= FINAL =================
    return_layers_second = orderedLayers;

    // ==================================
    return {
        summary: summary_obj,
        layers: orderedLayers, //return_layers,
        proximityLayers: proximityOnlyLayers, // Non-intersecting layers within 2NM
        maxAltitude: tempMaxAltitude,
        proceed_next: proceed,
        authorizations_array: operation, //authorizations_time_schedule_container,
        timestamps: { // to use within Home.js, not for the backend
            startDateTime: selected_start_date_obj.toString().split(' GMT')[0],
            endDateTime: selected_end_date_obj.toString().split(' GMT')[0], // formatDateToLongString
            duration: duration,
        }
    };

}

// Helper function to extract UASFM airspace and ft value from the description
function extractDetails(description) {
    const pattern = /\(([^)]+)\)\s*CLASS\s*(\w+)\s*up\s*to\s*(\d+)\s*ft/gi;
    const matches = [];
    let match;

    while ((match = pattern.exec(description)) !== null) {
        const airspace = match[1]; // Extract airspace (e.g., CMA/KCMA)
        const classType = match[2]; // Extract class (e.g., D)
        const ft = parseInt(match[3], 10); // Extract ft value (e.g., 200)
        matches.push({airspace, class: classType, ft});
    }

    return matches;
}

function isEpochOverlap(dateTimeStartString, dateTimeEndString, timeZone, epochStart, epochEnd) {


    //console.log(dateTimeStartString);
    //console.log(dateTimeEndString);

    // Convert start and end date-time strings to JavaScript Date objects in the specified timezone
    ///const dateTimeStartInTZ = new Date(new Date(dateTimeStartString).toLocaleString('en-US', {timeZone}));
    //const dateTimeEndInTZ = new Date(new Date(dateTimeEndString).toLocaleString('en-US', {timeZone}));

    ////console.log(dateTimeStartInTZ);
    ////console.log(dateTimeEndInTZ);

    // Get the epoch times (Unix timestamps) of the start and end date-times in milliseconds
    const dateTimeStartEpoch = convertToUTCEpoch(dateTimeStartString, timeZone); //dateTimeStartInTZ.getTime();
    const dateTimeEndEpoch = convertToUTCEpoch(dateTimeEndString, timeZone); //dateTimeEndInTZ.getTime();

    //console.log(epochStart+" "+epochEnd);
    //console.log(dateTimeStartEpoch +" " + dateTimeEndEpoch);

    // Check if there is any overlap between the two ranges
    return (dateTimeStartEpoch <= epochEnd && dateTimeEndEpoch >= epochStart);
}

function convertToUTCEpoch(dateTimeString, timeZone) {
    // Replace space with 'T' to match ISO format
    const isoDateTimeString = dateTimeString.replace(' ', 'T');

    // Parse the date-time string with the given time zone using luxon
    const dateInTimeZone = DateTime.fromISO(isoDateTimeString, {zone: timeZone});

    // Convert the date-time to UTC
    const utcTime = dateInTimeZone.toUTC();

    // Convert UTC time to epoch milliseconds
    return utcTime.toMillis();
}

// function convertToUTCEpoch(dateTimeString, timeZone) {
//     // Parse the date manually from the input string (assuming it's in yyyy-mm-dd hh:mm format)
//     const [datePart, timePart] = dateTimeString.split(' ');
//     const [year, month, day] = datePart.split('-');
//     const [hour, minute] = timePart.split(':');
//
//     // Use Intl.DateTimeFormat to format the date in the specified time zone
//     const formatter = new Intl.DateTimeFormat('en-US', {
//         timeZone,
//         year: 'numeric',
//         month: '2-digit',
//         day: '2-digit',
//         hour: '2-digit',
//         minute: '2-digit',
//         second: '2-digit',
//         hour12: false
//     });
//
//     // Create a Date object using the input date-time string as if it's in the given time zone
//     const fakeDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
//
//     // Format the date-time to the specified time zone
//     const parts = formatter.formatToParts(fakeDate).reduce((acc, part) => {
//         acc[part.type] = part.value;
//         return acc;
//     }, {});
//
//     // Create the formatted date string based on the parts from the time zone conversion
//     const formattedDate = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
//
//     //console.log(formattedDate);
//
//     // Convert the date string (now interpreted as UTC) to epoch time in milliseconds
//     return new Date(formattedDate).getTime();
// }


// get the intersecting polygon of user selected area and airspace
function getGeoIntersection(mapref, selectedArea, layer_id, objectid) {

    //console.log("querying:" + layer_id + " " + objectid);

    // Query rendered features from the specified layer, filtering by OBJECTID
    const features = mapref.queryRenderedFeatures({
        layers: [layer_id], // Replace with the ID of the layer you want to query
        filter: ['==', ['get', 'OBJECTID'], objectid] // Filter for matching OBJECTID
    });


    //console.log(selectedArea.geometry.coordinates);

    // Use Turf.js to find the intersection between the filtered layer features and the GeoJSON polygon
    const intersections = features.map(feature => {
        //console.log(feature.geometry);
        //console.log(selectedArea.geometry);
        let poly1;
        if (feature.geometry.type === 'Polygon') {
            poly1 = turfhelp.polygon(feature.geometry.coordinates);
        } else if (feature.geometry.type === 'MultiPolygon') {
            poly1 = turfhelp.multiPolygon(feature.geometry.coordinates);
        } else {
            throw new Error('Unsupported geometry type: ' + feature.geometry.type);
        }

        let poly2;
        if (selectedArea.geometry.type === 'Polygon') {
            poly2 = turfhelp.polygon(selectedArea.geometry.coordinates);
        } else if (selectedArea.geometry.type === 'MultiPolygon') {
            poly2 = turfhelp.multiPolygon(selectedArea.geometry.coordinates);
        } else {
            throw new Error('Unsupported geometry type: ' + selectedArea.geometry.type);
        }
        return turf.intersect(turfhelp.featureCollection([poly1, poly2]));
    }).filter(intersection => intersection !== null); // Filter out null values (no intersection)

    //console.log(intersections);
    // Remove duplicate geometries (Optimization Item #7: Use custom hash instead of JSON.stringify)
    const uniqueIntersections = [
        ...new Map(intersections.map(intersection => [createGeometryHashKey(intersection), intersection])).values()
    ]
        // Filter out geometries with invalid coordinates (fewer than 4 positions per ring)
        .filter(intersection => {
            if (!intersection || !intersection.geometry) {
                return false;
            }
            const isValid = isValidPolygonCoordinates(intersection.geometry.coordinates, intersection.geometry.type);
            if (!isValid) {
            }
            return isValid;
        });

    //**do not remove console log
    // console.log(uniqueIntersections);
    //

    // Check if there are any valid intersections
    if (!uniqueIntersections || uniqueIntersections.length === 0) {
        return null;
    }

// JavaScript
//     if (uniqueIntersections.length < 2) {
//         console.error("Union operation requires at least 2 geometries.");
//         return null;
//     }

    let combined = JSON.parse(JSON.stringify(uniqueIntersections[0]));

    for (let i = 1; i < uniqueIntersections.length; i++) {
        const geomA = combined;
        const geomB = uniqueIntersections[i];
        if (
            geomA &&
            geomB &&
            (geomA.geometry.type === 'Polygon' || geomA.geometry.type === 'MultiPolygon') &&
            (geomB.geometry.type === 'Polygon' || geomB.geometry.type === 'MultiPolygon')
        ) {

            // Validate that both geometries have valid coordinates (at least 4 positions per ring)
            const isGeomAValid = isValidPolygonCoordinates(geomA.geometry.coordinates, geomA.geometry.type);
            const isGeomBValid = isValidPolygonCoordinates(geomB.geometry.coordinates, geomB.geometry.type);

            if (!isGeomAValid || !isGeomBValid) {
                continue;
            }

            delete geomA.properties;
            delete geomB.properties;

            // Ensure geomA and geomB are valid turf polygon features
            const polyA = geomA.geometry.type === 'MultiPolygon'
                ? turfhelp.multiPolygon(geomA.geometry.coordinates)
                : turfhelp.polygon(geomA.geometry.coordinates);
            const polyB = geomB.geometry.type === 'MultiPolygon'
                ? turfhelp.multiPolygon(geomB.geometry.coordinates)
                : turfhelp.polygon(geomB.geometry.coordinates);
            // console.log(polyA);
            // console.log(polyB);

            const unionResult = union(turfhelp.featureCollection([polyA, polyB]));
            if (unionResult) {
                combined = unionResult;
            }
        }
    }

    // Ensure combined geometry exists before processing
    if (!combined || !combined.geometry) {
        return null;
    }

    // Round all coordinates to 5 decimal places (~1m precision) to match the
    // user-drawn polygon sanitisation and prevent high-precision turf intersection
    // points (e.g. -90.08947725504807) from reaching the LAANC submission.
    const COORD_PRECISION = 5;
    const roundRing = ring =>
        ring.map(coord => [
            Number(coord[0].toFixed(COORD_PRECISION)),
            Number(coord[1].toFixed(COORD_PRECISION))
        ]);

    if (combined.geometry.type === 'Polygon') {
        combined.geometry.coordinates = combined.geometry.coordinates.map(roundRing);
    } else if (combined.geometry.type === 'MultiPolygon') {
        combined.geometry.coordinates = combined.geometry.coordinates.map(
            polygon => polygon.map(roundRing)
        );
    }

    // Deduplicate consecutive coincident vertices (rounding may have collapsed
    // near-identical turf intersection points into the same coordinate).
    if (combined.geometry.type === 'Polygon') {
        const coords = combined.geometry.coordinates[0];
        if (coords.length > 2) {
            const first = coords[0];
            const last = coords[coords.length - 1];
            // Filter only the middle coordinates for duplicates
            const middle = coords.slice(1, -1).filter(
                (coord, index, self) =>
                    self.findIndex(
                        (c) => c[0] === coord[0] && c[1] === coord[1]
                    ) === index
            );
            combined.geometry.coordinates[0] = [first, ...middle, last];
        }
    } else if (combined.geometry.type === 'MultiPolygon') {
        combined.geometry.coordinates = combined.geometry.coordinates.map(rings => {
            const coords = rings[0];
            if (coords.length > 2) {
                const first = coords[0];
                const last = coords[coords.length - 1];
                const middle = coords.slice(1, -1).filter(
                    (coord, index, self) =>
                        self.findIndex(
                            (c) => c[0] === coord[0] && c[1] === coord[1]
                        ) === index
                );
                rings[0] = [first, ...middle, last];
            }
            return rings;
        });
    }

    // Get only LAANC layer IDs to avoid querying 3D model layers
    const mapLayers = mapref.getStyle().layers;
    const laancLayerIds = mapLayers
        .filter(layer => {
            // Exclude 3D model layers (type: 'model') - they cause getModel() errors
            if (layer.type === 'model') return false;
            return layer.id.toLowerCase().includes("laanc-");
        })
        .map(layer => layer.id);


    // query for underlying UASFM
    let all_uasfm_layers = [];
    // For MultiPolygon, coordinates[0] is the first polygon's rings array — use coordinates[0][0] for its exterior ring.
    // For Polygon, coordinates[0] is the exterior ring directly.
    let authorization_coords = combined.geometry.type === 'MultiPolygon'
        ? combined.geometry.coordinates[0][0]
        : combined.geometry.coordinates[0];

    // Densify polygon vertices to match circle vertex density (~150 points)
    // This improves UASFM query accuracy by reducing boundary vertex proportion
    const TARGET_VERTEX_COUNT = 150;
    const originalVertexCount = authorization_coords.length - 1; // Exclude closing point

    if (originalVertexCount < TARGET_VERTEX_COUNT) {

        const densified = [];
        const pointsPerSegment = Math.ceil(TARGET_VERTEX_COUNT / originalVertexCount);

        // Process each segment (excluding the closing point)
        for (let i = 0; i < originalVertexCount; i++) {
            const start = authorization_coords[i];
            const end = authorization_coords[i + 1];

            // Add start point
            densified.push(start);

            // Add intermediate points along this segment
            for (let j = 1; j < pointsPerSegment; j++) {
                const fraction = j / pointsPerSegment;
                const interpolated = [
                    start[0] + (end[0] - start[0]) * fraction,
                    start[1] + (end[1] - start[1]) * fraction
                ];
                densified.push(interpolated);
            }
        }

        // Add closing point (same as first point)
        densified.push(densified[0]);

        authorization_coords = densified;
    } else {
    }

    authorization_coords.forEach(coord => {
        // //console.log(coord);
        const [lng, lat] = coord;
        const sizeInDegrees = 0.00001;
        // Define the southwest (bottom-left) and northeast (top-right) corners of the bounds
        const southwest = new mapboxgl.LngLat(lng - sizeInDegrees / 2, lat - sizeInDegrees / 2); // Bottom-left corner
        const northeast = new mapboxgl.LngLat(lng + sizeInDegrees / 2, lat + sizeInDegrees / 2); // Top-right corner
        let bounds = new mapboxgl.LngLatBounds(southwest, northeast);

        const northEastPointPixel = mapref.project(bounds.getNorthEast());
        const southWestPointPixel = mapref.project(bounds.getSouthWest());

        // Only query if we have LAANC layers (skip 3D models)
        if (laancLayerIds.length === 0) {
            return;
        }

        const features = mapref.queryRenderedFeatures([southWestPointPixel, northEastPointPixel], {
            layers: laancLayerIds,
            validate: false
        });

        const temp_layers = features
            .filter(feature => feature.source === 'composite')
            .filter(feature => {
                const env = process.env.REACT_APP_ENV;
                const id = feature.layer.id.toLowerCase();
                if (['qa', 'dev'].includes(env)) {
                    return id.includes("qa-laanc-uasfm");
                }
                return id.includes("laanc-uasfm");
            })
            .map(feature => ({layerId: feature.layer.id.toLowerCase(), properties: feature.properties}));
        all_uasfm_layers.push(...temp_layers)
    });

    for (let i = 0; i < authorization_coords.length; i++) {
        const point1 = authorization_coords[i];
        const point2 = authorization_coords[(i + Math.floor(authorization_coords.length / 2)) % authorization_coords.length];

        // Create a LngLatBounds object
        const bounds = new mapboxgl.LngLatBounds(
            [Math.min(point1[0], point2[0]), Math.min(point1[1], point2[1])],
            [Math.max(point1[0], point2[0]), Math.max(point1[1], point2[1])]
        );
        //bounds_array.push(bounds);
        const northEastPointPixel = mapref.project(bounds.getNorthEast());
        const southWestPointPixel = mapref.project(bounds.getSouthWest());

        // Only query if we have LAANC layers (skip 3D models)
        if (laancLayerIds.length === 0) {
            continue;
        }

        const features = mapref.queryRenderedFeatures([southWestPointPixel, northEastPointPixel], {
            layers: laancLayerIds,
            validate: false
        });
        const temp_layers = features
            .filter(feature => feature.source === 'composite')
            .filter(feature => {
                const env = process.env.REACT_APP_ENV;
                const id = feature.layer.id.toLowerCase();
                if (['qa', 'dev'].includes(env)) {
                    return id.includes("qa-laanc-uasfm");
                }
                return id.includes("laanc-uasfm");
            })
            .map(feature => ({layerId: feature.layer.id.toLowerCase(), properties: feature.properties}));

        all_uasfm_layers.push(...temp_layers);
    }
    // Deduplicate UASFM layers (Optimization Item #7: Use Map instead of Set with JSON.stringify)
    const uniqueLayerNames = [
        ...new Map(all_uasfm_layers.map(item => [
            `${item.layerId}|${JSON.stringify(item.properties)}`,
            item
        ])).values()
    ];
    //console.log(uniqueLayerNames);
    combined.properties.facilityMaps = uniqueLayerNames.map((layer) => ({
        globalId: layer.properties.GLOBALID,
        lastEditDate: convertToISODate(layer.properties.LAST_EDIT)
    }));

    // CRITICAL: Ensure only Polygon geometry types are submitted (not MultiPolygon)
    // Convert MultiPolygon to single Polygon by taking the largest polygon
    if (combined.geometry.type === 'MultiPolygon') {

        // Find the polygon with the largest area
        let largestPolygon = null;
        let largestArea = 0;

        combined.geometry.coordinates.forEach((polygonCoords, index) => {
            const tempPolygon = turfhelp.polygon(polygonCoords);
            const polygonArea = area(tempPolygon);


            if (polygonArea > largestArea) {
                largestArea = polygonArea;
                largestPolygon = polygonCoords;
            }
        });

        // Convert to single Polygon
        if (largestPolygon) {
            combined.geometry.type = 'Polygon';
            combined.geometry.coordinates = largestPolygon;
        } else {
            return null;
        }
    }

    // Final validation: Ensure geometry type is Polygon
    if (combined.geometry.type !== 'Polygon') {
        return null;
    }


    return {
        "type": "FeatureCollection", "features": [combined]
    };
}


// Convert feet to degrees
function feetToDegrees(feet, latitude) {
    const metersPerDegreeLat = 111320; // meters in one degree of latitude
    const metersPerDegreeLon = 111320 * Math.cos(latitude * Math.PI / 180); // meters in one degree of longitude at given latitude
    const feetToMeters = 0.3048; // 1 foot = 0.3048 meters

    const latShift = feet * feetToMeters / metersPerDegreeLat;
    const lonShift = feet * feetToMeters / metersPerDegreeLon;

    return {latShift, lonShift};
}

// Calculate the centroid of the polygon
function calculateCentroid(polygon) {
    let x = 0, y = 0, z = 0;
    const numPoints = polygon.length;

    polygon.forEach(([lon, lat]) => {
        const latRad = lat * Math.PI / 180;
        const lonRad = lon * Math.PI / 180;

        x += Math.cos(latRad) * Math.cos(lonRad);
        y += Math.cos(latRad) * Math.sin(lonRad);
        z += Math.sin(latRad);
    });

    x /= numPoints;
    y /= numPoints;
    z /= numPoints;

    const lon = Math.atan2(y, x);
    const hyp = Math.sqrt(x * x + y * y);
    const lat = Math.atan2(z, hyp);

    return [lon * 180 / Math.PI, lat * 180 / Math.PI]; // Return in degrees
}

// Shrink the polygon by moving points toward the centroid
function shrinkPolygon(polygon, shrinkFeet) {
    const centroid = calculateCentroid(polygon);

    return polygon.map(([lon, lat]) => {
        const {latShift, lonShift} = feetToDegrees(shrinkFeet, lat);

        // Move each point towards the centroid
        let newLon = lon + (centroid[0] - lon) * (lonShift / Math.abs(centroid[0] - lon));
        let newLat = lat + (centroid[1] - lat) * (latShift / Math.abs(centroid[1] - lat));

        return [newLon, newLat];
    });
}


// function getGeoIntersection(mapref, selectedArea, layer_id_array, objectid_array) {
//     ////console.log("querying:" + layer_id + " " + objectid);
//
//     //console.log(layer_id_array)
//     //console.log(objectid_array);
//
//     // Query rendered features from the specified layer, filtering by OBJECTID
//     const features = mapref.queryRenderedFeatures({
//         layers: layer_id_array, // Replace with the ID of the layer you want to query // [layer_id]
//         filter: ['in', 'OBJECTID', ...objectid_array] // Filter for matching OBJECTID //  filter: ['==', ['get', 'OBJECTID'], objectid] // Filter for matching OBJECTID
//     });
//
//     //console.log(features);
//
//     // Use Turf.js to find the intersection between the filtered layer features and the GeoJSON polygon
//     const intersections = features.map(feature => {
//         // Deep copy of the geometries to avoid modifying the original data
//         let poly1 = turfhelp.polygon(JSON.parse(JSON.stringify(feature.geometry.coordinates)));
//         let poly2 = turfhelp.polygon(JSON.parse(JSON.stringify(selectedArea.geometry.coordinates)));
//
//         // Find intersection using Turf.js and ensure deep copy
//         const intersection = turf.intersect(turfhelp.featureCollection([poly1, poly2]));
//
//         // Return deep copy of intersection to avoid mutating shared references
//         return intersection ? JSON.parse(JSON.stringify(intersection)) : null;
//     }).filter(intersection => intersection !== null); // Filter out null values (no intersection)
//
//     //console.log(intersections);
//
//     // Remove duplicate geometries
//     const uniqueIntersections = Array.from(
//         new Set(
//             intersections.map((feature) => JSON.stringify(feature.geometry))
//         )
//     ).map((geometry) =>
//         intersections.find((feature) => JSON.stringify(feature.geometry) === geometry)
//     );
//
//     //console.log(uniqueIntersections);
//
//     // Initialize combined polygon with a deep copy of the first polygon
//     let combined = uniqueIntersections.length > 0 ? JSON.parse(JSON.stringify(uniqueIntersections[0])) : null;
//     //let combined = deepClone(uniqueIntersections[0]);
//
//     // Iteratively combine each polygon in the array using Turf.js union
//     for (let i = 1; i < uniqueIntersections.length; i++) {
//         if (combined && uniqueIntersections[i]) {
//             try {
//
//                 combined = turfunion.union(
//                     JSON.parse(JSON.stringify(combined)),
//                     JSON.parse(JSON.stringify(uniqueIntersections[i]))
//                 );
//                 // Perform the union and ensure we clone the result
//                 //combined = turfunion.union(_.cloneDeep(combined), _.cloneDeep(uniqueIntersections[i])); // Or use deepClone
//             } catch (error) {
//                 console.error("Error during union operation:", error);
//             }
//         }
//     }
//
//
//     // Check if the result is a Polygon or MultiPolygon
//     if (combined && combined.geometry.type === 'Polygon') {
//         //console.log('The result is a Polygon.');
//     } else if (combined && combined.geometry.type === 'MultiPolygon') {
//         //console.log('The result is a MultiPolygon.');
//     }
//
//     // Return the result as a GeoJSON FeatureCollection
//     return combined ? {"type": "FeatureCollection", "features": [combined]} : null;
// }


// Alternatively, custom deepClone if not using Lodash
function deepClone(obj) {
    return Array.isArray(obj)
        ? obj.map(deepClone)
        : obj !== null && typeof obj === 'object'
            ? Object.keys(obj).reduce((acc, key) => {
                acc[key] = deepClone(obj[key]);
                return acc;
            }, {})
            : obj;
}

// support functions for authorizations
function timeStringToMinutes(timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
}

function calculateDuration(startTime, endTime) {
    const startMinutes = timeStringToMinutes(startTime);
    const endMinutes = timeStringToMinutes(endTime);

    // Calculate the difference in minutes
    let duration = endMinutes - startMinutes;

    // Handle the case where the end time is on the next day
    if (duration < 0) {
        duration += 24 * 60; // Add 24 hours in minutes
    }

    return duration;
}

function minutesToTimeString(minutes) {
    const hours = Math.floor(minutes / 60) % 24;
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

function addWrapAroundSegments(start, end) {
    if (end < start) {
        return [
            {start, end: 24 * 60},
            {start: 0, end}
        ];
    }
    return [{start, end}];
}

function findOverlaps(segments1, segments2) {
    const overlaps = [];
    for (const seg1 of segments1) {
        for (const seg2 of segments2) {
            const overlapStart = Math.max(seg1.start, seg2.start);
            const overlapEnd = Math.min(seg1.end, seg2.end);
            if (overlapStart < overlapEnd) {
                overlaps.push({
                    overlapStart: minutesToTimeString(overlapStart),
                    overlapEnd: minutesToTimeString(overlapEnd)
                });
            }
        }
    }
    return overlaps;
}

function timeRangesOverlap(range1Start, range1End, range2Start, range2End) {
    const start1 = timeStringToMinutes(range1Start);
    const end1 = timeStringToMinutes(range1End);
    const start2 = timeStringToMinutes(range2Start);
    const end2 = timeStringToMinutes(range2End);

    // Create segments for wrap-around times
    const segments1 = addWrapAroundSegments(start1, end1);
    const segments2 = addWrapAroundSegments(start2, end2);

    // Find all overlapping segments
    return findOverlaps(segments1, segments2);
}

function addMinutesToDate(date, minutes) {
    // Get the current time in milliseconds
    const currentTimeInMs = date.getTime();

    // Calculate the number of milliseconds to add
    const minutesInMs = minutes * 60 * 1000;

    // Add the milliseconds to the current time
    const newTimeInMs = currentTimeInMs + minutesInMs;

    // Create a new Date object with the updated time
    return new Date(newTimeInMs);
}

// add a day to yyyy-mm-dd string
function addDayToDate(dateString) {
    // Split the date string into components
    const [year, month, day] = dateString.split('-').map(Number);

    // Create a new Date object
    const date = new Date(year, month - 1, day); // Note: months are zero-indexed

    // Add one day
    date.setDate(date.getDate() + 1);

    // Format the resulting date back to "yyyy-mm-dd"
    const newYear = date.getFullYear();
    const newMonth = String(date.getMonth() + 1).padStart(2, '0'); // months are zero-indexed
    const newDay = String(date.getDate()).padStart(2, '0');

    return `${newYear}-${newMonth}-${newDay}`;
}

// for unique arrays
function addObjectIfNotExists_UASFM(array, item, comparator) {

    if (!array.some(existingItem => comparator(existingItem, item))) {
        array.push(item);
    }
}

// for unique arrays
function addObjectIfNotExists(array, item, comparator) {
    if (
        item.LOWER_VAL === 0 && // Check if LOWER_VAL is 0
        !array.some(existingItem => comparator(existingItem, item)) // Check for duplicates
    ) {
        array.push(item);
    }
}

//convert yyyy-mm-dd hh:mm to isostring
function reformatToISOString(dateTimeString) {
    // Split the date and time parts
    const [datePart, timePart] = dateTimeString.split(' ');

    // Further split the date and time into their components
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    // Create a new Date object
    const date = new Date(Date.UTC(year, month - 1, day, hours, minutes));

    // Convert the Date object to an ISO string
    return date.toISOString().split('.')[0] + 'Z';
}

//convert mm/dd/yyyy to isostring
function convertToISODate(dateString) {
    // Split the input string into components
    const [month, day, year] = dateString.split('/');

    // Pad the month and day with leading zeros if needed and format the date
    const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    return formattedDate;
}

// convert a time from 12-hour format (e.g., 4:48:45 PM) to 24-hour format (e.g., 16:48)
function convertTo24Hour(time) {
    // Extract the parts of the time string
    const [timePart, modifier] = time.split(' ');

    // Extract the hours, minutes, and seconds
    let [hours, minutes, seconds] = timePart.split(':');

    // Convert hours to a number
    hours = parseInt(hours, 10);

    // Adjust hours based on AM/PM
    if (modifier === 'PM' && hours !== 12) {
        hours += 12;
    } else if (modifier === 'AM' && hours === 12) {
        hours = 0;
    }

    // Format hours and minutes to ensure they are two digits
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');

    // Return the formatted time in hh:mm 24-hour format
    return `${formattedHours}:${formattedMinutes}`;
}

// date object to yyyy-mm-dd hh:mm
function formatDateToYYYYMMDDHHMM(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
}

//TODO: consolidate duplicate
//convert Sun Aug 25 2024 07:26:00 format to yyyy-MM-ddTHH:mm:ssZ
function formatDateToISOStringWithoutTimezone(date) {
    // Get the components of the date
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // Combine the components into the desired format
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
}

//Formats a Date object to 'Monday, July 15, 2024 11:06 AM'.
function formatDateToLongString(date) {
    const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    };
    const timeOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    };

    const formattedDate = date.toLocaleDateString('en-US', options);
    const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

    return `${formattedDate} ${formattedTime}`;
}


// const TFR_TIMEZONE_MAP = {
//     EDT: "America/New_York",
//     EST: "America/New_York",
//     PDT: "America/Los_Angeles",
//     PST: "America/Los_Angeles",
//     CDT: "America/Chicago",
//     CST: "America/Chicago",
//     MDT: "America/Denver",
//     MST: "America/Denver",
//     AKDT: "America/Anchorage",
//     AKST: "America/Anchorage",
//     HST: "Pacific/Honolulu",
//     UTC: "UTC"
// };

const TFR_TIMEZONE_MAP = {
    // Current abbreviations
    EDT: "America/New_York",
    EST: "America/New_York",
    PDT: "America/Los_Angeles",
    PST: "America/Los_Angeles",
    CDT: "America/Chicago",
    CST: "America/Chicago",
    MDT: "America/Denver",
    MST: "America/Denver",
    AKDT: "America/Anchorage",
    AKST: "America/Anchorage",
    HST: "Pacific/Honolulu",
    UTC: "UTC",

    // Continental US
    "America/New_York": "America/New_York",
    "America/Detroit": "America/Detroit",
    "America/Kentucky/Louisville": "America/Kentucky/Louisville",
    "America/Kentucky/Monticello": "America/Kentucky/Monticello",
    "America/Indiana/Indianapolis": "America/Indiana/Indianapolis",
    "America/Indiana/Vincennes": "America/Indiana/Vincennes",
    "America/Indiana/Winamac": "America/Indiana/Winamac",
    "America/Indiana/Marengo": "America/Indiana/Marengo",
    "America/Indiana/Petersburg": "America/Indiana/Petersburg",
    "America/Indiana/Vevay": "America/Indiana/Vevay",
    "America/Chicago": "America/Chicago",
    "America/Indiana/Tell_City": "America/Indiana/Tell_City",
    "America/Indiana/Knox": "America/Indiana/Knox",
    "America/Menominee": "America/Menominee",
    "America/North_Dakota/Center": "America/North_Dakota/Center",
    "America/North_Dakota/New_Salem": "America/North_Dakota/New_Salem",
    "America/North_Dakota/Beulah": "America/North_Dakota/Beulah",
    "America/Denver": "America/Denver",
    "America/Boise": "America/Boise",
    "America/Phoenix": "America/Phoenix",
    "America/Los_Angeles": "America/Los_Angeles",

    // Alaska
    "America/Anchorage": "America/Anchorage",
    "America/Juneau": "America/Juneau",
    "America/Sitka": "America/Sitka",
    "America/Metlakatla": "America/Metlakatla",
    "America/Yakutat": "America/Yakutat",
    "America/Nome": "America/Nome",
    "America/Adak": "America/Adak",

    // Hawaii
    "Pacific/Honolulu": "Pacific/Honolulu",

    // US Territories
    "Pacific/Guam": "Pacific/Guam",
    "Pacific/Saipan": "Pacific/Saipan",
    "America/Puerto_Rico": "America/Puerto_Rico",
    "America/St_Thomas": "America/St_Thomas"
};


function isDateTimeInRange(dateTime, duration, startRange, endRange, locationTimezone, tfrTimezoneAbbr) {

    // Validate required parameters
    if (!dateTime || !startRange || !endRange || duration === undefined) {
        throw new Error('Missing required datetime parameters');
    }

    // Parse the input datetime in its local timezone
    const localDate = DateTime.fromFormat(dateTime, "yyyy-MM-dd HH:mm", {
        zone: locationTimezone,
    });
    if (!localDate.isValid) {
        throw new Error(`Invalid dateTime: ${localDate.invalidExplanation}`);
    }

    // Calculate end time by adding duration in minutes
    const localEndDate = localDate.plus({minutes: duration});

    // Resolve the TFR timezone (convert abbreviation to IANA)
    const tfrTimezone = TFR_TIMEZONE_MAP[tfrTimezoneAbbr?.toUpperCase()] || "UTC";

    // Parse TFR start and end times
    // Parse TFR start and end times using the same format as dateTime
    const startDate = DateTime.fromFormat(startRange, "yyyy-MM-dd HH:mm", {zone: tfrTimezone});
    const endDate = DateTime.fromFormat(endRange, "yyyy-MM-dd HH:mm", {zone: tfrTimezone});

    if (!startDate.isValid || !endDate.isValid) {
        throw new Error('Invalid TFR datetime range');
    }

    // Convert all to UTC for accurate comparison
    const localStartUTC = localDate.toUTC();
    const localEndUTC = localEndDate.toUTC();
    const tfrStartUTC = startDate.toUTC();
    const tfrEndUTC = endDate.toUTC();

    // Check for overlap: two time ranges overlap if start1 < end2 && start2 < end1
    return localStartUTC < tfrEndUTC && tfrStartUTC < localEndUTC;
}

function isDateTimeInRangeFrequency(dateTime, duration, startRange, endRange, locationTimezone, tfrTimezone) {

    // Parse dateTime with the location timezone
    const localDate = DateTime.fromFormat(dateTime, "yyyy-MM-dd HH:mm", {zone: locationTimezone});

    if (!localDate.isValid) {
        throw new Error(`Invalid dateTime: ${localDate.invalidExplanation}`);
    }

    // Calculate end time by adding duration in minutes
    const localEndDate = localDate.plus({minutes: duration});

    // Parse start and end ranges with TFR timezone
    const tfrZone = TFR_TIMEZONE_MAP[tfrTimezone] || "UTC";
    const startDate = DateTime.fromFormat(startRange, "yyyy-MM-dd HH:mm", {zone: tfrZone});
    const endDate = DateTime.fromFormat(endRange, "yyyy-MM-dd HH:mm", {zone: tfrZone});

    if (!startDate.isValid || !endDate.isValid) {
        throw new Error('Invalid TFR datetime range');
    }

    // Extract time components from start and end ranges
    const startTime = {hour: startDate.hour, minute: startDate.minute};
    const endTime = {hour: endDate.hour, minute: endDate.minute};

    // Convert operation start and end times to TFR timezone for comparison
    const operationStartInTfrZone = localDate.setZone(tfrZone);
    const operationEndInTfrZone = localEndDate.setZone(tfrZone);

    // Check if operation dates fall within the TFR date range
    const operationStartDateOnly = operationStartInTfrZone.startOf('day');
    const operationEndDateOnly = operationEndInTfrZone.startOf('day');
    const startDateOnly = startDate.startOf('day');
    const endDateOnly = endDate.startOf('day');

    if (operationEndDateOnly < startDateOnly || operationStartDateOnly > endDateOnly) {
        return false;
    }

    // Check for each day in the operation period if there's overlap with the daily TFR time window
    let currentDate = operationStartDateOnly;
    while (currentDate <= operationEndDateOnly && currentDate <= endDateOnly) {
        if (currentDate >= startDateOnly) {
            // Get operation times for this specific day
            const dayStart = currentDate.equals(operationStartDateOnly) ? operationStartInTfrZone : currentDate;
            const dayEnd = currentDate.equals(operationEndDateOnly) ? operationEndInTfrZone : currentDate.endOf('day');

            // Check if operation time overlaps with TFR daily time window
            const operationStartMinutes = dayStart.hour * 60 + dayStart.minute;
            const operationEndMinutes = dayEnd.hour * 60 + dayEnd.minute;
            const tfrStartMinutes = startTime.hour * 60 + startTime.minute;
            const tfrEndMinutes = endTime.hour * 60 + endTime.minute;

            // Handle overnight TFR time ranges (e.g., 22:00 to 06:00)
            if (tfrEndMinutes < tfrStartMinutes) {
                // For overnight ranges, check if operation overlaps with either:
                // 1. The evening portion (from tfrStart to end of day)
                // 2. The morning portion (from start of day to tfrEnd)
                if ((operationStartMinutes < 1440 && operationEndMinutes > tfrStartMinutes) ||
                    (operationStartMinutes < tfrEndMinutes)) {
                    return true;
                }
            } else {
                // Regular same-day time range
                if (operationStartMinutes < tfrEndMinutes && operationEndMinutes > tfrStartMinutes) {
                    return true;
                }
            }
        }
        currentDate = currentDate.plus({days: 1});
    }

    return false;
}


// Function to sort by CLASS and check for matches with the first item
const sortAndCheckMatchWithFirst = (list) => {
    // Sort the list by CLASS
    const sortedList = list.sort((a, b) => a.CLASS.localeCompare(b.CLASS));

    // Get the CLASS value of the first item
    const firstItemClass = sortedList[0]?.CLASS;

    // Check for matches with the CLASS of the first item
    const matchingItems = sortedList.filter(item => item.CLASS === firstItemClass);

    return {
        sortedList,
        firstItemClass,
        matchingItems
    };
};


// Function to convert APT fields into an array of objects, including additional arrays
function convertFieldsToStructuredData(properties) {
    const aptArray = [];
    const airsCount = properties.AIRS_COUNT;
    const airspaceArray = [];

    const nameArray = [];
    const faaidArray = [];
    const icaoArray = [];
    const enabledArray = [];
    const laancArray = [];

    // Convert APT fields to arrays and objects
    for (let i = 1; i <= airsCount; i++) {
        const aptObject = {};

        const name = properties[`APT${i}_NAME`];
        const faaiD = properties[`APT${i}_FAAID`];
        const icao = properties[`APT${i}_ICAO`];
        const enabled = properties[`APT${i}_Enabled`];
        const laanc = properties[`APT${i}_LAANC`];

        // Only include the property if it is not an empty string ("")
        if (name && name !== "") {
            aptObject.name = name;
            nameArray.push(name);
        }
        if (faaiD && faaiD !== "") {
            aptObject.faaiD = faaiD;
            faaidArray.push(faaiD);
        }
        if (icao && icao !== "") {
            aptObject.icao = icao;
            icaoArray.push(icao);
        }
        if (enabled && enabled !== "") {
            aptObject.enabled = enabled;
            enabledArray.push(enabled);
        }
        if (laanc && laanc !== "") {
            aptObject.laanc = laanc;
            laancArray.push(laanc);
        }

        // Only add the object to the array if it has at least one key
        if (Object.keys(aptObject).length > 0) {
            aptArray.push(aptObject);
        }
    }

    // Convert AIRSPACE fields to array
    for (let i = 1; i <= 5; i++) {
        const airspace = properties[`AIRSPACE_${i}`];
        if (airspace && airspace !== "") {
            airspaceArray.push(airspace);
        }
    }

    // Include non-array fields and the new arrays
    const result = {
        OBJECTID: properties.OBJECTID,
        LAST_EDIT: properties.LAST_EDIT,
        GLOBALID: properties.GLOBALID,
        AIRSPACE: airspaceArray,
        APTs: aptArray,
        names: nameArray,
        faaids: faaidArray,
        icaos: icaoArray,
        enabled: enabledArray,
        laanc: laancArray
    };

    return result;
}

// DC Special Flight Rules Area (SFRA) calculation
// within a 15miles and 30-mile radius of Ronald Reagan Washington National Airport
function isAnyCirclePointWithinRange(selectedArea_circle) {
    const dcCoords = [-77.0367, 38.8523]; // [longitude, latitude] Ronald R

    if (!selectedArea_circle?.geometry?.coordinates) return false;

    const points = selectedArea_circle.geometry.type === 'Polygon'
        ? selectedArea_circle.geometry.coordinates[0]
        : selectedArea_circle.geometry.coordinates;

    return points.some(coord => {
        const distMiles = distance(point(coord), point(dcCoords), {units: 'miles'});
        return distMiles >= 15.0 && distMiles <= 30.0;
    });
}

export default BusinessLogic;



